import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Play, RotateCcw, GraduationCap, ArrowRight, Maximize2, Minimize2 } from 'lucide-react';
import {
  trainingApi,
  TrainingStage,
  TrainingDemoAction,
  TrainingHotspot,
} from '@/api/endpoints';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Button from '@/components/ui/Button';

const DEFAULT_MOVE_MS = 1000;
const CLICK_PAUSE_MS = 500;

// Сколько ждать ПОСЛЕ завершения этапа, прежде чем переключить плеер в
// 'browse' (= показать «Перейти к следующему» / «Пройти самостоятельно»).
// Юзер просил подержать «успех» дольше — раньше 600мс (после анимации)
// и 0мс (после клика/инпута) — слишком быстро, кнопки появлялись до того,
// как юзер успевал отпустить курсор и понять что зашёл правильно.
const RESULT_HOLD_MS = 2000;

type Phase = 'browse' | 'idle' | 'animating' | 'interactive' | 'success';

// Demo flavor — admin = упрощённая версия, director = с полным доступом.
// Variant определяется префиксом iframeUrl у этапа. Old stages с префиксами
// /demoadminpanel/, /demo/, /admin-demo/ считаются admin-flavor.
type DemoVariant = 'admin' | 'director';
const ADMIN_PREFIX_RE = /^\/(demoadminpanel|demo|admin-demo)(\/|$)/i;
const DIRECTOR_PREFIX_RE = /^\/admin-demo-director(\/|$)/i;
const detectVariant = (url?: string | null): DemoVariant =>
  url && DIRECTOR_PREFIX_RE.test(url) ? 'director' : 'admin';
// Section regex matches BOTH flavors so the URL-poll → stage-switch effect
// works regardless of which demo is loaded in the iframe.
// Демо смонтировано под nginx-алиасом (/admin-demo|/admin-demo-director|
// /demoadminpanel|/demo), но его собственный history-роутер при навигации
// делает pushState на "/adminpanel/<route>" (его внутренний basename),
// ТЕРЯЯ алиас. Поэтому после первой навигации внутри демо путь в iframe
// становится, например, "/adminpanel/reports" — и старый SECTION_RE, знавший
// только алиасы, его не узнавал → URL-poll слеп → хлебная крошка не следовала
// за разделом демо. Считаем «маршрут», срезая И алиас, И basename "/adminpanel".
const DEMO_PREFIX_RE = /^\/(admin-demo-director|demoadminpanel|admin-demo|demo)(?=\/|$)/i;
const DEMO_BASENAME_RE = /^\/adminpanel(?=\/|$)/i;
function routeOf(path: string | null | undefined): string {
  let p = (path || '').split('?')[0].split('#')[0];
  p = p.replace(DEMO_PREFIX_RE, '');
  p = p.replace(DEMO_BASENAME_RE, '');
  return p.replace(/^\/+|\/+$/g, '').toLowerCase();
}
const VARIANT_PERSIST_KEY = 'training-variant';

// Rewrite a saved stage iframe URL to the currently-selected demo flavor.
// Stage path stays the same (e.g. "/clubs"), only the base prefix changes:
// /demoadminpanel/clubs + variant=director → /admin-demo-director/clubs
// /admin-demo/clubs + variant=admin → /admin-demo/clubs
function applyVariantPrefix(url: string | null | undefined, variant: DemoVariant): string {
  const base = variant === 'director' ? '/admin-demo-director' : '/admin-demo';
  if (!url) return base + '/';
  // Strip ALL leading demo prefixes — legacy saves sometimes have doubled
  // prefixes like "/demoadminpanel/admin-demo-director/clubs" or the
  // obsolete "/adminpanel/" segment from when the admin SPA was mounted
  // there. With "/adminpanel" still in the URL nginx 404s on assets
  // (e.g. /admin-demo-director/adminpanel/config.js), falls back to
  // index.html, the browser parses HTML as JS, and the iframe stays white.
  let stripped = url;
  const prefixRe = /^\/(admin-demo-director|demoadminpanel|admin-demo|adminpanel|demo)(\/|$)/;
  while (prefixRe.test(stripped)) {
    stripped = stripped.replace(prefixRe, '/');
  }
  return base + (stripped.startsWith('/') ? stripped : '/' + stripped);
}

const TrainingPage: React.FC = () => {
  const qc = useQueryClient();
  const { data: rawStages = [], isLoading } = useQuery({
    queryKey: ['training-stages-public'],
    queryFn: trainingApi.listStages,
  });
  const { data: progress = [], isLoading: progressLoading } = useQuery({
    queryKey: ['training-progress'],
    queryFn: trainingApi.getMyProgress,
  });

  // `pageMissing` — внутри iframe демка отрендерила свою 404-страницу
  // (этап ссылается на путь, которого нет в выбранном flavor: например,
  // /reports есть только в admin-demo, но не в admin-demo-director).
  // Включаем баннер с предложением переключить вариант — раньше у юзера
  // просто чернел плеер с мутной «404 Страница не найдена» и непонятно
  // что делать.
  const [pageMissing, setPageMissing] = useState(false);

  // Variant chooser — persisted in localStorage so reload keeps the choice.
  // The variant is a PRESENTATION choice: same stage list, only the demo
  // flavor loaded in the iframe differs (admin = simpler menu, director =
  // full menu). Stage's saved iframe_url path is reused regardless.
  const [variant, setVariant] = useState<DemoVariant>(() => {
    try {
      const v = localStorage.getItem(VARIANT_PERSIST_KEY);
      return v === 'director' ? 'director' : 'admin';
    } catch { return 'admin'; }
  });
  useEffect(() => {
    try { localStorage.setItem(VARIANT_PERSIST_KEY, variant); } catch {}
  }, [variant]);

  // У администратора и директора — СВОЁ обучение (по просьбе юзера). Этап
  // помечен audience: 'admin' | 'director' | 'both'. В режиме «Админ» видны
  // этапы admin+both, в «Директор» — director+both. Старые этапы (audience
  // отсутствует или 'admin') остаются у администратора — ничего не теряется.
  const stages = useMemo(
    () =>
      rawStages.filter((s) => {
        const aud = (s as any).audience || 'admin';
        return aud === 'both' || aud === variant;
      }),
    [rawStages, variant],
  );

  // Yandex Passport popup killer ON PARENT FRAME. Popup рендерится поверх
  // body как absolute/fixed-блок с текстом «+7… Настроить… Закрыть».
  //
  // ВАЖНО: проверяем ТОЛЬКО недавно добавленные узлы (mutation.addedNodes),
  // а не весь DOM — предыдущая версия делала querySelectorAll('*') +
  // textContent на каждом, что для тяжёлой страницы давало O(N²) и весь
  // главный поток зависал ("страница не отвечает"). Плюс throttle/idle —
  // не чаще раза в 500ms.
  useEffect(() => {
    const phoneRe = /\+\s*7\s*\d{3}\s*\d{3}[\s\-]\d{2}[\s\-]\d{2}/;
    const KEY_RE = /Настроить.*Закрыть|Закрыть.*Настроить/s;
    const looksLikeYandexOverlay = (el: Element): boolean => {
      // Дешёвая проверка структуры до textContent: должен быть positioned-
      // блок маленького размера (≈popup), иначе пропускаем.
      try {
        const cs = (el.ownerDocument?.defaultView || window).getComputedStyle(el);
        if (cs.position !== 'fixed' && cs.position !== 'absolute') return false;
      } catch { return false; }
      const t = el.textContent || '';
      if (t.length > 600) return false; // popup обычно короткий
      return phoneRe.test(t) && KEY_RE.test(t);
    };
    const tryKill = (el: Element) => {
      if (!looksLikeYandexOverlay(el)) return;
      try { el.remove(); } catch {}
    };
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          tryKill(node as Element);
        });
      }
    });
    obs.observe(document.body, { childList: true, subtree: false });
    return () => obs.disconnect();
  }, []);

  // Fullscreen: разворачивает всю страницу обучения вместе с переключателями и
  // экраном демки. Браузерное API requestFullscreen — без сторонних либ.
  const rootRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else if (rootRef.current) {
      rootRef.current.requestFullscreen?.();
    }
  }, []);

  const completedIds = useMemo(() => new Set(progress.map((p) => p.stageId)), [progress]);
  const completedCount = completedIds.size;

  // Flatten tree: depth-first traversal so user progresses through sub-stages before next top-level
  const flatStages = useMemo(() => {
    const byParent = new Map<string | null, typeof stages>();
    stages.forEach((s) => {
      const list = byParent.get(s.parentId) || [];
      list.push(s);
      byParent.set(s.parentId, list);
    });
    byParent.forEach((list) => list.sort((a, b) => a.order - b.order));
    const out: { stage: typeof stages[number]; depth: number }[] = [];
    const walk = (parentId: string | null, depth: number) => {
      const children = byParent.get(parentId) || [];
      for (const c of children) {
        out.push({ stage: c, depth });
        walk(c.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [stages]);

  const total = flatStages.length;
  const firstUncompleted = flatStages.find((f) => !completedIds.has(f.stage.id));
  const [activeId, setActiveId] = useState<string | null>(null);
  // When goNext fires, freeze the iframe-URL-poll override for a few
  // seconds.  Otherwise the poll sees that iframe is still on the old
  // section, matches it to the previous top-level stage, and yanks
  // activeId back — making «Перейти к следующему этапу» look like it
  // does nothing (or worse, returns to the parent stage).
  const suppressUrlPollUntilRef = useRef<number>(0);

  useEffect(() => {
    // Не выставляем activeId, пока прогресс не загрузился — иначе на
    // быстром рендере (progress=[]) выбирается flatStages[0]=«Главная»,
    // а после прихода прогресса остаётся прежним. Из-за этого после
    // рефреша активный этап «гулял» между «Главной» и «Пополнением»
    // в зависимости от того, успел ли ответ API доехать к моменту mount.
    if (progressLoading) return;
    if (!activeId && flatStages.length) {
      setActiveId(firstUncompleted?.stage.id || flatStages[0].stage.id);
      return;
    }
    // If active stage no longer belongs to the chosen variant (variant
    // toggle happened), jump to first available stage of new variant.
    if (activeId && flatStages.length && !flatStages.some((f) => f.stage.id === activeId)) {
      setActiveId(firstUncompleted?.stage.id || flatStages[0].stage.id);
    }
  }, [activeId, flatStages, firstUncompleted, progressLoading]);

  // Родительский этап-РАЗДЕЛ (top-level, у которого есть под-этапы — например
  // «Главная») не должен играться как самостоятельный шаг с модалкой
  // «Перейти/Пройти самостоятельно»: при попадании на него (начальная загрузка
  // ИЛИ переход с предыдущего раздела через goNext) сразу проваливаемся в
  // первый под-этап. Раньше провал срабатывал только для «пустых» разделов —
  // но у «Главной» остались действия от записи демки, поэтому она зависала
  // отдельным шагом. Теперь правило простое: есть под-этапы → это раздел →
  // проваливаемся в первый под-этап (контент самого раздела игнорируем).
  useEffect(() => {
    if (!activeId) return;
    const cur = stages.find((s) => s.id === activeId);
    if (!cur || cur.parentId) return; // только top-level
    const firstChild = stages
      .filter((s) => s.parentId === cur.id)
      .sort((a, b) => a.order - b.order)[0];
    if (firstChild) setActiveId(firstChild.id);
  }, [activeId, stages]);

  const activeStage = stages.find((s) => s.id === activeId) || null;
  const activeIndex = activeStage ? flatStages.findIndex((f) => f.stage.id === activeStage.id) : -1;
  const canProceed = activeIndex >= 0 && activeIndex < flatStages.length - 1;

  const completeMutation = useMutation({
    mutationFn: (id: string) => trainingApi.markComplete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['training-progress'] }),
  });
  const resetMutation = useMutation({
    mutationFn: async () => {
      // Wipe ONLY demo-specific data. localStorage is shared by origin,
      // so we must NOT call .clear() — that would erase the parent app's session.
      // Demo's zustand stores use keys prefixed with "reborn-".
      const isDemoKey = (k: string | null) =>
        !!k && (k.startsWith('reborn-') || k.startsWith('demo-') || k.startsWith('recore-demo-'));

      const wipeStorage = (storage: Storage | undefined) => {
        if (!storage) return;
        const toRemove: string[] = [];
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (isDemoKey(k)) toRemove.push(k as string);
        }
        toRemove.forEach((k) => {
          try { storage.removeItem(k); } catch {}
        });
      };

      document.querySelectorAll('iframe').forEach((el) => {
        const f = el as HTMLIFrameElement;
        try {
          const w = f.contentWindow;
          if (w) {
            wipeStorage(w.localStorage);
            wipeStorage(w.sessionStorage);
          }
        } catch {}
        // Reload iframe so the auto-login script re-seeds clean state
        try {
          const src = f.src;
          if (src) {
            f.src = 'about:blank';
            requestAnimationFrame(() => { f.src = src; });
          }
        } catch {}
      });
      return trainingApi.resetProgress();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training-progress'] });
      if (stages.length) setActiveId(stages[0].id);
    },
  });

  // Poll iframe URL every 500ms — when path changes, switch active stage to
  // the top-level stage whose iframeUrl matches the same section.
  // ⚠️ MUST stay above any conditional return to satisfy rules-of-hooks
  // (variant switch to empty flavor would otherwise change hook count).
  useEffect(() => {
    if (!stages.length) return;
    const interval = setInterval(() => {
      // Suppress while user just manually advanced via goNext — iframe
      // hasn't repaginated yet, polling would yank them back.
      if (Date.now() < suppressUrlPollUntilRef.current) return;
      try {
        const iframes = document.querySelectorAll('iframe');
        // Детект «страница недоступна» в iframe демки — два сценария:
        //   1) Демка-роутер 404-ит (нет такого пути) — title содержит 404,
        //      body содержит «Страница не найдена».
        //   2) React-error-boundary поймал исключение и нарисовал свой
        //      фолбэк («Что-то пошло не так»). title не меняется, но
        //      <h2> в body — характерная строка.
        // В обоих случаях показываем баннер с кнопкой «пропустить этап».
        const firstIframe = iframes[0] as HTMLIFrameElement | undefined;
        const doc = firstIframe?.contentDocument;
        if (doc) {
          const title = doc.title || '';
          const bodyText = (doc.body?.innerText || '').slice(0, 600);
          const is404 =
            /404|not\s*found|не\s*найдена/i.test(title) &&
            /404|Страница\s+не\s+найдена|Page\s+not\s+found/i.test(bodyText);
          const isCrash = /Что-то\s+пошло\s+не\s+так|Something\s+went\s+wrong/i.test(bodyText);
          const missing = is404 || isCrash;
          setPageMissing((prev) => (prev !== missing ? missing : prev));
        }
        for (const f of Array.from(iframes)) {
          const path = (f as HTMLIFrameElement).contentWindow?.location?.pathname;
          if (!path) continue;
          const currentSection = routeOf(path);

          // Ищем подходящий этап — сначала среди ТОП-уровневых (раньше
          // искали только их). Если ни один не совпал, расширяем поиск
          // на ДОЧЕРНИЕ — частый случай: training-сценарий собран как
          // «один корневой этап с детьми по разделам».
          const sectionMatches = (s: TrainingStage) => routeOf(s.iframeUrl) === currentSection;
          let matches = stages.filter((s) => !s.parentId && sectionMatches(s));
          if (matches.length === 0) {
            matches = stages.filter((s) => s.parentId && sectionMatches(s));
          }
          if (matches.length === 0) break;

          // Если текущий активный этап (или его top-level) уже среди
          // совпадений — ничего не делаем, чтобы не дёргаться каждые 500мс.
          const current = stages.find((s) => s.id === activeId);
          const currentTopLevelId = current?.parentId || current?.id;
          if (
            matches.some(
              (m2) => m2.id === activeId || m2.id === currentTopLevelId,
            )
          )
            break;

          const target = matches[0];
          if (target.id !== activeId) {
            console.log('[training] URL changed → switch stage to', target.title);
            setActiveId(target.id);
          }
          break;
        }
      } catch {
        // cross-origin or transient — ignore
      }
    }, 500);
    return () => clearInterval(interval);
  }, [stages, activeId]);

  // Подэтапы текущего раздела впихиваем ПРЯМО в сайдбар самого демо — под
  // пункт меню активного top-level. Демо — отдельное SPA в same-origin
  // iframe, поэтому правим его DOM напрямую и держим в синхроне через
  // MutationObserver (демо перерисовывает свой сайдбар при навигации —
  // тогда переинъектим). На время своих правок наблюдатель отключаем,
  // чтобы не зациклиться на собственных мутациях.
  useEffect(() => {
    if (!stages.length) return;
    const MARK = 'data-training-substeps';
    const norm = (s: string) =>
      (s || '').toLowerCase().replace(/ё/g, 'е').replace(/[^а-яa-z0-9 ]/gi, '').replace(/\s+/g, ' ').trim();

    const active = stages.find((s) => s.id === activeId);
    const topLevel = active ? (active.parentId ? stages.find((s) => s.id === active.parentId) : active) : null;
    const subs = topLevel
      ? stages.filter((s) => s.parentId === topLevel.id).sort((a, b) => a.order - b.order)
      : [];

    let obs: MutationObserver | null = null;
    let attachTimer: ReturnType<typeof setTimeout> | undefined;
    let injectTimer: ReturnType<typeof setTimeout> | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let cancelled = false;

    const getDoc = (): Document | null => {
      const iframe = document.querySelector('iframe') as HTMLIFrameElement | null;
      try { return iframe?.contentDocument || null; } catch { return null; }
    };
    const removeInjected = (doc: Document) => {
      doc.querySelectorAll(`[${MARK}]`).forEach((n) => n.remove());
    };
    const findNavItem = (doc: Document): HTMLElement | null => {
      if (!topLevel) return null;
      const want = norm(topLevel.title);
      if (!want) return null;
      const els = Array.from(doc.querySelectorAll('a, button, [role="button"]')) as HTMLElement[];
      for (const el of els) {
        const t = norm((el.innerText || el.textContent || '').trim());
        if (t === want || t.startsWith(want + ' ')) return el;
      }
      return null;
    };
    // Меняем aria/expanded-классы и форсим клик по «Главная» (или любому
    // top-level), чтобы демо-аккордеон РАЗВЕРНУЛСЯ. Раньше юзер сам
    // долбил его 15 раз: коммит d2bf5ea авто-сваливает activeId в первый
    // под-этап, демо при этом сворачивает родителя, наш inject ставит
    // подэтапы рядом, но без клика по родителю он визуально схлопнут.
    const ensureExpanded = (doc: Document, item: HTMLElement | null) => {
      if (!item) return;
      try {
        // Реальный триггер с aria-expanded — это НЕ <li>, а внутренний
        // .ant-menu-submenu-title (role=menuitem). Раньше читали атрибут с li
        // (его там нет) → looksCollapsed всегда true → клик на КАЖДОЙ мутации
        // наблюдателя → аккордеон тоггл туда-сюда («долбить 15 раз» + мельтешение,
        // от которого этап и подэтапы наезжали). Берём элемент, у которого
        // aria-expanded реально есть.
        const scope = (item.closest('a, li, [role="button"]') as HTMLElement) || item;
        const trigger =
          (scope.matches('[aria-expanded]') ? scope : null) ||
          (scope.querySelector('[aria-expanded]') as HTMLElement | null) ||
          (scope.closest('[aria-expanded]') as HTMLElement | null);
        if (!trigger) return; // нет аккордеона — ничего не форсим (не вслепую)
        // Кликаем ТОЛЬКО когда явно свёрнут. Если уже развёрнут — не трогаем,
        // иначе свернём то, что только что открыли.
        if (trigger.getAttribute('aria-expanded') === 'false') {
          trigger.click?.();
        }
      } catch { /* демо не готово — переинъектим */ }
    };
    // Подпись текущего состояния — чтобы НЕ перевставлять блок без нужды.
    const buildSig = () =>
      `${topLevel?.id || ''}|${activeId || ''}|` +
      subs.map((s) => s.id + (completedIds.has(s.id) ? '1' : '0')).join(',');
    const reobserve = (d: Document) => {
      if (d.body && obs) obs.observe(d.body, { childList: true, subtree: true });
    };
    const inject = () => {
      if (cancelled) return;
      const doc = getDoc();
      if (!doc?.body) return;
      if (!subs.length || !topLevel) {
        obs?.disconnect();
        removeInjected(doc);
        reobserve(doc);
        return;
      }
      const sig = buildSig();
      const navItem = findNavItem(doc);
      // Пункт меню — это <div><a/></div>; вставляем СОСЕДНИМ блоком после
      // этого div, а не внутрь ссылки (чище, без наложения на след. пункт).
      const wrap = navItem ? ((navItem.closest('div') as HTMLElement) || navItem) : null;
      const existing = doc.querySelector(`[${MARK}]`) as HTMLElement | null;
      // ИДЕМПОТЕНТНОСТЬ: блок уже на месте, того же состава и сразу после пункта —
      // не трогаем. Раньше removeInjected+заново выполнялись на КАЖДУЮ мутацию
      // демо → мельтешение, наложение этап/подэтап и стирание вставки.
      if (
        existing &&
        existing.isConnected &&
        existing.getAttribute('data-sig') === sig &&
        wrap &&
        existing.previousElementSibling === wrap
      ) {
        return;
      }
      obs?.disconnect();
      try {
        removeInjected(doc);
        if (navItem && wrap) {
          ensureExpanded(doc, navItem); // no-op для обычных ссылок (нет aria)
          const box = doc.createElement('div');
          box.setAttribute(MARK, '1');
          box.setAttribute('data-sig', sig);
          box.style.cssText =
            'margin:2px 4px 8px 30px;padding-left:12px;border-left:1px solid rgba(99,102,241,0.35);display:flex;flex-direction:column;gap:2px;';
          subs.forEach((s) => {
            const done = completedIds.has(s.id);
            const isAct = s.id === activeId;
            const item = doc.createElement('button');
            item.type = 'button';
            item.textContent = s.title;
            item.style.cssText =
              'text-align:left;padding:6px 10px;border:0;border-radius:8px;cursor:pointer;font-size:12.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;' +
              `background:${isAct ? 'rgba(99,102,241,0.22)' : 'transparent'};` +
              `color:${isAct ? '#ffffff' : done ? '#86efac' : '#a1a1aa'};`;
            item.onmouseenter = () => { if (!isAct) item.style.background = 'rgba(255,255,255,0.06)'; };
            item.onmouseleave = () => { if (!isAct) item.style.background = 'transparent'; };
            item.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              setActiveId(s.id);
            });
            box.appendChild(item);
          });
          wrap.parentElement?.insertBefore(box, wrap.nextSibling);
        }
      } catch { /* DOM демо не готов — переинъектим по следующей мутации */ }
      reobserve(doc);
    };
    const scheduleInject = () => { clearTimeout(injectTimer); injectTimer = setTimeout(inject, 120); };
    const attach = () => {
      if (cancelled) return;
      const doc = getDoc();
      if (!doc?.body) { attachTimer = setTimeout(attach, 400); return; }
      obs = new MutationObserver(() => scheduleInject());
      inject();
      // Подстраховка: демо иногда перерисовывает сайдбар без отлавливаемых
      // мутаций (или стирает вставку) — мягко переинъектим раз в ~700мс.
      // Идемпотентность делает это дешёвым (no-op, если уже на месте).
      pollTimer = setInterval(() => { if (!cancelled) scheduleInject(); }, 700);
    };
    attach();

    return () => {
      cancelled = true;
      clearTimeout(attachTimer);
      clearTimeout(injectTimer);
      if (pollTimer) clearInterval(pollTimer);
      obs?.disconnect();
      const doc = getDoc();
      if (doc) { try { removeInjected(doc); } catch { /* ignore */ } }
    };
  }, [activeId, stages, completedIds, variant, isFullscreen]);

  if (isLoading) return <LoadingSpinner fullPage text="Загрузка обучения..." />;

  if (stages.length === 0) {
    // Если общий список не пуст, но в текущем варианте этапов нет — даём
    // тогглу переключиться. Иначе показываем обычный empty-state.
    const otherVariant: DemoVariant = variant === 'director' ? 'admin' : 'director';
    const hasOtherVariant = rawStages.some((s) => {
      const aud = (s as any).audience || 'admin';
      return aud === 'both' || aud === otherVariant;
    });
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        {/* Тоггл всегда виден, чтобы юзер мог сменить роль и увидеть свои
            этапы — даже когда текущий вариант пуст. */}
        <div className="mb-6 inline-flex items-center rounded-md border border-line bg-fill-2 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setVariant('admin')}
            className={`px-3 py-1.5 rounded transition-colors ${
              variant === 'admin' ? 'bg-bg-card text-fg-primary' : 'text-zinc-400 hover:text-fg-primary'
            }`}
          >
            Админ
          </button>
          <button
            type="button"
            onClick={() => setVariant('director')}
            className={`px-3 py-1.5 rounded transition-colors ${
              variant === 'director' ? 'bg-bg-card text-fg-primary' : 'text-zinc-400 hover:text-fg-primary'
            }`}
          >
            Директор
          </button>
        </div>
        <GraduationCap className="mb-4 h-12 w-12 text-zinc-700" />
        <h2 className="text-lg font-semibold text-fg-primary">
          {hasOtherVariant
            ? `Для роли «${variant === 'director' ? 'Директор' : 'Админ'}» обучение пока не настроено`
            : 'Обучение пока не настроено'}
        </h2>
        <p className="mt-1 text-sm text-zinc-500">
          {hasOtherVariant
            ? `Попробуй переключиться на «${variant === 'director' ? 'Админ' : 'Директор'}» — для него этапы уже есть.`
            : 'Этапы будут добавлены администратором.'}
        </p>
        {hasOtherVariant && (
          <button
            type="button"
            onClick={() => setVariant(otherVariant)}
            className="mt-4 px-4 py-2 rounded-lg bg-accent text-accent-text text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Переключиться на «{otherVariant === 'director' ? 'Директор' : 'Админ'}»
          </button>
        )}
      </div>
    );
  }

  const handleStageComplete = (stageId: string) => {
    if (!completedIds.has(stageId)) {
      completeMutation.mutate(stageId);
    }
  };

  const goNext = () => {
    if (!activeStage) return;
    // Дайте новой стадии 3 секунды загрузиться, прежде чем URL-poll
    // снова начнёт принудительно подгонять activeId к iframe.location.
    suppressUrlPollUntilRef.current = Date.now() + 3000;
    if (activeStage.parentId) {
      // Мы на ПОДэтапе. Идём к следующему подэтапу того же родителя.
      // Последний подэтап → сразу к следующему top-level (минуя возврат
      // к parent'у — юзер хотел бесшовный переход).
      const siblings = stages
        .filter((s) => s.parentId === activeStage.parentId)
        .sort((a, b) => a.order - b.order);
      const myIdx = siblings.findIndex((s) => s.id === activeStage.id);
      if (myIdx >= 0 && myIdx < siblings.length - 1) {
        setActiveId(siblings[myIdx + 1].id);
        return;
      }
      // Последний подэтап — прыгаем на следующий top-level
      const topLevels = stages.filter((s) => !s.parentId).sort((a, b) => a.order - b.order);
      const parentIdx = topLevels.findIndex((s) => s.id === activeStage.parentId);
      if (parentIdx >= 0 && parentIdx < topLevels.length - 1) {
        setActiveId(topLevels[parentIdx + 1].id);
      } else {
        // Совсем последний — возвращаемся на родителя (нет «следующего»)
        setActiveId(activeStage.parentId);
      }
    } else {
      // Top-level: переходим к следующему top-level.
      const topLevels = stages.filter((s) => !s.parentId).sort((a, b) => a.order - b.order);
      const myIdx = topLevels.findIndex((s) => s.id === activeStage.id);
      if (myIdx >= 0 && myIdx < topLevels.length - 1) {
        setActiveId(topLevels[myIdx + 1].id);
      }
    }
  };

  // Normalize Russian text for fuzzy matching (ё → е, lowercase, trim, drop punctuation)
  const normalizeRu = (s: string) =>
    (s || '')
      .toLowerCase()
      .replace(/ё/g, 'е')
      .replace(/[^а-яa-z0-9 ]/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

  // When user clicks a sidebar item in the demo (in browse phase),
  // switch active stage to the matching stage by title. Search order:
  // top-level first, then child stages (для сценариев «один корневой
  // этап с детьми по разделам» — иначе клики мимо).
  const handleSidebarClickText = (text: string) => {
    const normalized = normalizeRu(text);
    if (!normalized) return;
    const findIn = (pool: TrainingStage[]) =>
      pool.find((s) => normalizeRu(s.title) === normalized) ||
      pool.find((s) => {
        const t = normalizeRu(s.title);
        return normalized.startsWith(t + ' ') || normalized === t;
      }) ||
      pool.find((s) => {
        const t = normalizeRu(s.title);
        return t.length >= 3 && normalized.includes(t);
      });
    const topLevel = stages.filter((s) => !s.parentId);
    const children = stages.filter((s) => s.parentId);
    const match = findIn(topLevel) || findIn(children);
    if (match && match.id !== activeId) {
      console.log('[training] sidebar match:', text, '→ stage:', match.title);
      setActiveId(match.id);
    } else if (!match) {
      console.log('[training] sidebar text no stage match:', text, 'norm:', normalized);
    }
  };

  return (
    <div
      ref={rootRef}
      className={
        isFullscreen
          ? 'fixed inset-0 z-50 bg-zinc-950 p-3 flex flex-col gap-2 overflow-hidden'
          // Страница обучения должна влезать в один экран без скролла.
          // - h = высота viewport минус sticky-хедер (~64px на md+, ~56px на sm)
          // - flex flex-col + overflow-hidden — внутренности распределяются
          //   по вертикали и не вылазят
          // - правый/нижний/верхний negative-margin компенсирует p-3/4/6
          //   <main> в ClientLayout (но слева оставляем gutter под бейдж
          //   уведомлений у sticky-сайдбара)
          : 'flex flex-col gap-2 overflow-hidden h-[calc(100dvh-72px)] md:h-[calc(100dvh-80px)] -mr-3 md:-mr-4 lg:-mr-6 -mb-3 md:-mb-4 lg:-mb-6 -mt-3 md:-mt-4 lg:-mt-6 pt-3 md:pt-4 lg:pt-6'
      }
    >
      {/* Breadcrumb + variant switcher */}
      <div className="flex items-center justify-between px-1 gap-2 flex-nowrap">
        <div className="flex items-center gap-2 min-w-0">
          <GraduationCap className="h-6 w-6 text-indigo-400 flex-shrink-0" />
          <span className="text-xs uppercase tracking-wider text-zinc-500 hidden sm:inline">
            Этап {activeIndex + 1}/{total}
          </span>
          {activeStage && (
            <h2 className="text-xl md:text-2xl font-bold text-fg-primary tracking-[-0.02em] truncate">
              {activeStage.title}
            </h2>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Variant switcher — admin vs director flavor of the demo.
              Не фильтрует стейджи: один и тот же урок можно посмотреть в обеих
              версиях демо. Меняется только бандл в iframe. */}
          <div className="inline-flex items-center rounded-md border border-line bg-fill-2 p-0.5 text-xs">
            <button
              type="button"
              onClick={() => setVariant('admin')}
              className={`px-3 py-1.5 rounded transition-colors ${
                variant === 'admin'
                  ? 'bg-fill-2 text-fg-primary'
                  : 'text-zinc-400 hover:text-fg-primary'
              }`}
              title="Версия для администраторов"
            >
              Админ
            </button>
            <button
              type="button"
              onClick={() => setVariant('director')}
              className={`px-3 py-1.5 rounded transition-colors ${
                variant === 'director'
                  ? 'bg-fill-2 text-fg-primary'
                  : 'text-zinc-400 hover:text-fg-primary'
              }`}
              title="Версия для директора (больше функций)"
            >
              Директор
            </button>
          </div>
          <button
            onClick={toggleFullscreen}
            className="text-xs text-zinc-400 hover:text-fg-primary flex items-center gap-1.5 transition-colors"
            title={isFullscreen ? 'Свернуть' : 'На весь экран'}
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
            <span className="hidden md:inline">{isFullscreen ? 'Свернуть' : 'На весь экран'}</span>
          </button>
          <button
            onClick={() => {
              if (confirm('Сбросить прогресс обучения и стереть данные в демо (введённые номера, выбранных юзеров и т.п.)?')) {
                resetMutation.mutate();
              }
            }}
            className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 transition-colors"
            title="Сбросить прогресс"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            <span className="hidden md:inline">Сбросить</span>
          </button>
          {/* Слот, в который StagePlayer порталит «Повторить»/«Далее» —
              так все кнопки управления живут в ОДНОЙ строке, а демо-плеер
              получает освободившуюся вертикаль (становится крупнее). */}
          <div id="training-ctrl-slot" className="flex items-center gap-2" />
        </div>
      </div>

      {/* Баннер «этап не открывается в этом варианте демки». Включается
          когда внутри iframe демка отрисовала свою 404 — обычно потому,
          что путь авторился под admin, а сейчас выбран director (или
          наоборот). Кнопка моментально переключает вариант — баннер
          снимется на следующем тике polling. */}
      {pageMissing && activeStage ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-amber-100 flex items-start gap-3 flex-wrap">
          <span className="text-lg leading-none mt-0.5">⚠️</span>
          <div className="flex-1 text-sm min-w-[220px]">
            <div className="font-semibold mb-0.5">
              Этап «{activeStage.title}» не открылся в варианте «{variant === 'director' ? 'Директор' : 'Админ'}»
            </div>
            <div className="text-amber-200/80">
              Либо в этой демке нет такой страницы, либо она упала с ошибкой при загрузке. Попробуйте другой вариант или пропустите этап.
            </div>
          </div>
          <div className="flex gap-2 flex-wrap shrink-0">
            <button
              type="button"
              onClick={() => setVariant(variant === 'director' ? 'admin' : 'director')}
              className="rounded bg-amber-400/20 hover:bg-amber-400/30 border border-amber-400/40 px-3 py-1.5 text-xs font-semibold text-amber-50"
            >
              Переключить на «{variant === 'director' ? 'Админ' : 'Директор'}»
            </button>
            <button
              type="button"
              onClick={goNext}
              className="rounded bg-fill-2 hover:bg-fill-2 border border-line px-3 py-1.5 text-xs font-semibold text-fg-primary"
            >
              Пропустить этап
            </button>
          </div>
        </div>
      ) : null}

      {/* Демо-плеер на всю ширину. Подэтапы текущего раздела впихиваются
          ПРЯМО в сайдбар демо (см. эффект инъекции выше), а не отдельной
          панелью обучения. */}
      <div className={isFullscreen ? 'w-full flex-1 min-h-0 flex flex-col' : 'w-full flex-1 min-h-0 flex flex-col'}>
        {activeStage ? (
          <StagePlayer
            // Re-mount iframe when variant or fullscreen changes so the
            // demo bundle re-renders at the new container dimensions
            key={variant + ':' + (isFullscreen ? 'fs' : 'norm')}
            stage={activeStage}
            variant={variant}
            fillHeight={true}
            alreadyCompleted={completedIds.has(activeStage.id)}
            onComplete={() => handleStageComplete(activeStage.id)}
            canProceed={canProceed}
            onNext={goNext}
            stageNumber={activeIndex + 1}
            totalStages={stages.length}
            onSidebarClick={handleSidebarClickText}
          />
        ) : null}
      </div>
    </div>
  );
};

// =====================================================
// Stage player: animation → spotlight → wait for click
// =====================================================
interface StagePlayerProps {
  stage: TrainingStage;
  variant: DemoVariant;
  fillHeight?: boolean;
  alreadyCompleted: boolean;
  onComplete: () => void;
  canProceed: boolean;
  onNext: () => void;
  stageNumber: number;
  totalStages: number;
  onSidebarClick?: (text: string) => void;
}

const StagePlayer: React.FC<StagePlayerProps> = ({
  stage,
  variant,
  fillHeight = false,
  alreadyCompleted,
  onComplete,
  canProceed,
  onNext,
  stageNumber,
  totalStages,
  onSidebarClick,
}) => {
  // ALL stages start in "browse" phase — user picks the stage and clicks "Start".
  const [phase, setPhase] = useState<Phase>('browse');
  // DOM-узел в строке-крошке, куда порталим кнопки «Повторить»/«Далее»,
  // чтобы они были в одной строке с остальными контролами (а не отдельным
  // рядом). Берём после монтирования — слот рендерит родитель.
  const [ctrlSlot, setCtrlSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setCtrlSlot(document.getElementById('training-ctrl-slot'));
  }, []);
  const [actionIdx, setActionIdx] = useState(0);
  const [cursor, setCursor] = useState({ x: 0, y: 0, clicking: false, transitionMs: 0 });
  const [autoHotspot, setAutoHotspot] = useState<TrainingHotspot | null>(null);
  // sidebarBounds инициализируем ДЕФОЛТНЫМ значением (не null) — иначе
  // BrowseOverlay не отрендерится в первый момент входа в browse-phase,
  // и пользователь не увидит «Пройти самостоятельно» пока iframe грузится.
  // Эффект ниже подтюнит границы как только найдёт реальный sidebar.
  const [sidebarBounds, setSidebarBounds] = useState<{ x: number; y: number; w: number; h: number }>({ x: 0, y: 0, w: 230, h: 1080 });
  // Fixed virtual viewport — must match admin editor's DEMO_W/DEMO_H
  const imgSize = { w: 1920, h: 1080 };
  const [hint, setHint] = useState(false);
  // Inline overlay state for action-types that didn't exist when this
  // player was first written: `comment` (yellow speech-bubble), `highlight`
  // (dashed accent box), `hotspot` with waitForUser (pulsing target that
  // pauses playback until user clicks inside it).
  const [activeComment, setActiveComment] = useState<{ at: [number, number]; text: string } | null>(null);
  const [activeHighlight, setActiveHighlight] = useState<{ box: [number, number, number, number] } | null>(null);
  // Zoom-наезд на область демки. Плеер применяет CSS transform на iframe-wrapper:
  // scale + translate так, чтобы box оказался по центру с увеличением. По истечении
  // duration сбрасываем в null — обратное движение анимируется через transition.
  const [activeZoom, setActiveZoom] = useState<{ box: [number, number, number, number] } | null>(null);
  const [waitingHotspot, setWaitingHotspot] = useState<{
    box: [number, number, number, number];
    expectedInput?: string;
  } | null>(null);
  // Self-practice mode: список разрешённых boxes (отдельных rect для каждого
  // action'а стейджа). Рендерится как SVG с маской — «дырки» в этих местах,
  // всё остальное физически перехватывает клики через pointer-events.
  const [allowedBoxes, setAllowedBoxes] = useState<Array<[number, number, number, number]> | null>(null);
  // stage.comments — слайдшоу: один комментарий за раз, меняется каждые 5 сек
  // только во время animating-фазы. На browse/success/interactive не показываем
  // (иначе все 4 пузыря висят разом на success-экране).
  const [stageCommentIdx, setStageCommentIdx] = useState(0);
  // Динамически snapped box: реальные пиксельные границы input/кнопки
  // под центром hotspot'а в DOM iframe'а. Используем вместо raw box[]
  // если демка-iframe сдвинула UI с момента калибровки в редакторе.
  const [snappedBox, setSnappedBox] = useState<[number, number, number, number] | null>(null);
  const pendingHotspotResolveRef = useRef<(() => void) | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Initial cursor position = coords of the first action (so it doesn't fly in from (0,0))
  // Cursor always starts from the center of the demo viewport (1920×1080)
  const initialCursorPos = { x: 960, y: 540 };

  // Auto-find sidebar nav item for top-level stages without manual hotspot.
  // The stage title must match the sidebar item's text (e.g., "Главная", "Бронирование").
  //
  // ВАЖНО: запускается ТОЛЬКО если у стейджа нет своих action'ов И нет comments.
  // Иначе после прохождения action'ов плеер ВСЕГДА просит «нажми на N в сайдбаре» —
  // это раздражает на стейджах вроде «Шаг 1 - Пополнение», где автор УЖЕ
  // прописал все нужные действия.
  useEffect(() => {
    // ОТКЛЮЧЕНО: autoHotspot создавал «нажми на N в сайдбаре» для стейджей
    // без actions/comments. На практике это завершало обучение преждевременно
    // (любой клик в зоне auto-detected сайдбар-кнопки засчитывался как
    // «прошёл стейдж») и иногда крашило демку. User: «хотспот завершает
    // сразу обучение и из-за него краши часто» — поэтому полностью убираем
    // automatic. Стейджи без actions завершаются обычной кнопкой «Далее».
    setAutoHotspot(null);
    return undefined as any;
    // eslint-disable-next-line no-unreachable
    let stop = false;
  }, [stage.id, stage.title, stage.hotspot, stage.parentId]);

  // Effective hotspot — manual one wins, otherwise auto-found sidebar item
  const effectiveHotspot: TrainingHotspot | null = stage.hotspot ?? autoHotspot;

  // Listen for sidebar clicks in any phase — keeps the top breadcrumb
  // synced with whichever section the user is currently viewing.
  useEffect(() => {
    if (!onSidebarClick) return;
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handler = (e: Event) => {
      console.log('[training] iframe click event fired, target=', e.target);
      let el = e.target as HTMLElement | null;
      for (let i = 0; i < 8 && el; i++) {
        const innerText = ((el as HTMLElement).innerText || '').trim();
        const textContent = (el.textContent || '').trim();
        const ariaLabel = (el.getAttribute && el.getAttribute('aria-label')) || '';
        const title = (el.getAttribute && el.getAttribute('title')) || '';
        const candidates = [innerText, textContent, ariaLabel, title]
          .map((s) => (s || '').trim())
          .filter((s) => s.length > 0 && s.length < 80 && !s.includes('\n\n'));
        for (const text of candidates) {
          // First clean candidate that looks like a sidebar label
          console.log('[training] sidebar click candidate:', text);
          onSidebarClick(text);
          return;
        }
        el = el.parentElement;
      }
    };

    let cleanup: (() => void) | null = null;
    let stop = false;
    const tryAttach = () => {
      if (stop) return;
      const doc = iframe.contentDocument;
      if (!doc) {
        console.log('[training] sidebar listener: iframe not ready, retry in 300ms');
        setTimeout(tryAttach, 300);
        return;
      }
      doc.addEventListener('click', handler, true);
      console.log('[training] sidebar listener ATTACHED to iframe document');
      cleanup = () => {
        try { doc.removeEventListener('click', handler, true); } catch {}
      };
    };
    tryAttach();
    return () => { stop = true; cleanup?.(); };
  }, [onSidebarClick, stage.iframeUrl]);

  // Drive demo SPA navigation when the parent switches to a new stage.
  // iframe.src is intentionally `lockedSrc` (mounted once); without this
  // explicit call, going Главная→Бронирование leaves the demo on Главная,
  // and TrainingPage's iframe-URL poll then snaps activeId back.
  useEffect(() => {
    if (!stage.iframeUrl) return;
    let cancelled = false;
    const tryNavigate = () => {
      if (cancelled) return;
      try {
        const win = iframeRef.current?.contentWindow as any;
        const nav = win?.navigate;
        if (typeof nav === 'function') {
          // Strip the alias prefix so demo's BASE_PATH bridge re-prepends correctly.
          const path = (stage.iframeUrl || '')
            .replace(/^https?:\/\/[^/]+/, '')
            .replace(/^\/(admin-demo-director|demoadminpanel|admin-demo|demo)(\/|$)/, '/')
            || '/';
          nav(path);
          return;
        }
      } catch {/* same-origin not ready yet */}
      // SPA not mounted yet — retry in 200 ms.
      setTimeout(tryNavigate, 200);
    };
    tryNavigate();
    return () => { cancelled = true; };
  }, [stage.id, stage.iframeUrl]);

  // Detect sidebar bounds inside iframe (for browse-phase cutout)
  useEffect(() => {
    if (phase !== 'browse') return;
    let stop = false;
    const tryFind = () => {
      if (stop) return;
      const doc = iframeRef.current?.contentDocument;
      if (!doc?.body) {
        setTimeout(tryFind, 400);
        return;
      }
      const selectors = ['nav', 'aside', '[role="navigation"]', '[class*="sidebar"]', '[class*="Sidebar"]'];
      let found: HTMLElement | null = null;
      for (const sel of selectors) {
        const el = doc.querySelector(sel) as HTMLElement | null;
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.width > 100 && rect.height > 200 && rect.left < 200) {
          found = el;
          break;
        }
      }
      if (!found) {
        // Fallback: walk up from "Главная" link to find common nav parent
        const links = Array.from(doc.querySelectorAll('a, button, [role="button"]'));
        const homeLink = links.find((el) => {
          const t = ((el as HTMLElement).innerText || el.textContent || '').trim();
          return t === 'Главная' || t.startsWith('Главная ');
        }) as HTMLElement | undefined;
        if (homeLink) {
          let parent: HTMLElement | null = homeLink;
          for (let i = 0; i < 6 && parent; i++) {
            parent = parent.parentElement;
            if (parent) {
              const r = parent.getBoundingClientRect();
              if (r.width > 100 && r.height > 200 && r.left < 200) {
                found = parent;
                break;
              }
            }
          }
        }
      }
      if (found) {
        const r = found.getBoundingClientRect();
        // ВЫРЕЗ ПОД САЙДБАР шире самого сайдбара на ~16px. Раньше было
        // «− 30» (cutout уже сайдбара) и затемнение залезало на правый
        // край: бейджи «2», иконки. Юзер: «затемнение сильно залезает
        // на сайдбар» → +16px правее.
        setSidebarBounds({
          x: 0,
          y: 0,
          w: Math.max(180, Math.round(r.right) + 16),
          h: imgSize.h,
        });
        return;
      }
      // Fallback
      setSidebarBounds({ x: 0, y: 0, w: 230, h: imgSize.h });
    };
    tryFind();
    return () => { stop = true; };
  }, [phase, stage.id]);

  // Reset on stage change — every stage waits in 'browse' for user to press Start.
  // Хранение прошлого stage.id нужно чтобы НЕ запускать «закрытие модалок»
  // на первом монтировании — иначе мы стучим Escape в свежезагруженный
  // iframe ещё до того, как там что-то отрендерилось, и в редких случаях
  // браузер прокидывает Escape в parent — снимая фокус с кнопок плеера
  // (репорт: «Старт не работает по кнопке»).
  const prevStageIdRef = useRef<string | null>(null);
  useEffect(() => {
    clearTimer();
    setPhase('browse');
    setActionIdx(0);
    setCursor({ x: initialCursorPos.x, y: initialCursorPos.y, clicking: false, transitionMs: 0 });
    setActiveComment(null);
    setActiveHighlight(null);
    setWaitingHotspot(null);
    setAllowedBoxes(null);
    setStageCommentIdx(0);
    pendingHotspotResolveRef.current = null;

    const isFirstMount = prevStageIdRef.current === null;
    prevStageIdRef.current = stage.id;
    if (isFirstMount) return;

    // Закрываем модал в демке через Escape ВНУТРИ contentDocument iframe.
    // Без принудительного click() по close-кнопкам — он иногда триггерил
    // навигацию демки и ломал состояние.
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc?.body) {
        const ev = new KeyboardEvent('keydown', {
          key: 'Escape', code: 'Escape', keyCode: 27, which: 27,
          bubbles: true, cancelable: true,
        });
        doc.body.dispatchEvent(ev);
      }
    } catch { /* cross-origin или iframe не готов */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage.id]);


  // Snap waitingHotspot's box to the actual interactive element (input/button)
  // под центром коробки. Этим компенсируем сдвиги контента демки между
  // моментом авторской калибровки и реальным рендером на клиенте.
  useEffect(() => {
    setSnappedBox(null);
    if (!waitingHotspot || !iframeRef.current) return;
    const iframe = iframeRef.current;
    let cancelled = false;
    const tryFit = () => {
      if (cancelled) return;
      let doc: Document | null = null;
      try { doc = iframe.contentDocument; } catch { doc = null; }
      if (!doc) { setTimeout(tryFit, 200); return; }
      const [bx, by, bw, bh] = waitingHotspot.box;
      const cx = bx + bw / 2;
      const cy = by + bh / 2;
      // Probe multiple points to catch the underlying element even if center
      // sits on a label/decorator instead of the actual input
      const probes: Array<[number, number]> = [
        [cx, cy], [bx + 12, cy], [bx + bw - 12, cy],
        [cx, by + 12], [cx, by + bh - 12],
      ];
      let target: HTMLElement | null = null;
      for (const [px, py] of probes) {
        const el = doc.elementFromPoint(px, py) as HTMLElement | null;
        if (!el) continue;
        let curr: HTMLElement | null = el;
        for (let hop = 0; hop < 5 && curr && curr !== doc.body; hop++) {
          if (curr.tagName === 'INPUT' || curr.tagName === 'TEXTAREA' || curr.isContentEditable ||
              curr.tagName === 'BUTTON' || curr.getAttribute('role') === 'button') {
            target = curr; break;
          }
          curr = curr.parentElement;
        }
        if (target) break;
      }
      if (!target) { setTimeout(tryFit, 200); return; }
      const r = target.getBoundingClientRect();
      // Coords уже в системе iframe-viewport (1920×1080 благодаря ScaledIframe).
      setSnappedBox([
        Math.round(r.left),
        Math.round(r.top),
        Math.round(r.width),
        Math.round(r.height),
      ]);
    };
    // Small delay для уверенности что демка-DOM смонтирована/анимация завершена
    const t = setTimeout(tryFit, 80);
    return () => { cancelled = true; clearTimeout(t); };
  }, [waitingHotspot]);

  // Listen for clicks inside the iframe while we're waiting on an inline
  // `hotspot` action with waitForUser=true. Click inside the box (plus optional
  // matching input) → completes the action and animation resumes.
  useEffect(() => {
    if (!waitingHotspot || !iframeRef.current) return;
    const iframe = iframeRef.current;
    const [bx, by, bw, bh] = waitingHotspot.box;
    const inHotspot = (x: number, y: number) => x >= bx && x <= bx + bw && y >= by && y <= by + bh;
    const expected = waitingHotspot.expectedInput?.trim();
    const normalize = (s: string) => (s || '').replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();
    const want = expected ? normalize(expected) : '';
    const onClick = (e: MouseEvent) => {
      // Click-advance работает только если hotspot НЕ ждёт ввода (просто
      // клик-зона). Если задан expectedInput — продвинемся через onInput
      // когда значение совпадёт.
      if (want) return;
      if (!inHotspot(e.clientX, e.clientY)) return;
      pendingHotspotResolveRef.current?.();
    };
    const onInput = (e: Event) => {
      if (!want) return;
      const t = e.target as HTMLInputElement | HTMLTextAreaElement | null;
      if (!t) return;
      const raw = (t as HTMLInputElement).value ?? (t as HTMLElement).textContent ?? '';
      const v = normalize(String(raw));
      if (v && (v === want || v.includes(want))) {
        // Небольшая пауза — даём демке отрисовать результат поиска
        // (юзер увидит найденные аккаунты), и только потом advance.
        setTimeout(() => pendingHotspotResolveRef.current?.(), 600);
      }
    };
    let doc: Document | null = null;
    try { doc = iframe.contentDocument; } catch { doc = null; }
    if (!doc) return;
    doc.addEventListener('click', onClick, true);
    doc.addEventListener('input', onInput, true);

    // ── Keystroke gating: если задан expectedInput, разрешаем печатать
    // только символы, которые продолжают будущее значение как ПРЕФИКС
    // ожидаемой строки. Любой "лишний" символ блокируется. Так оператор
    // НЕ может ввести что-то кроме того, что просит обучение.
    const expectedRaw = waitingHotspot.expectedInput?.trim() || '';
    const onKeyDown = (e: KeyboardEvent) => {
      if (!expectedRaw) return;
      const t = e.target as HTMLInputElement | HTMLTextAreaElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;
      // Разрешаем все служебные клавиши (стрелки, Backspace, Delete, Home/End,
      // Tab, Esc, Enter, всё что длинной > 1 символа) и сочетания с Ctrl/Meta.
      if (e.key.length > 1) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Какое получится value ПОСЛЕ нажатия?
      const current = t.value;
      const start = t.selectionStart ?? current.length;
      const end = t.selectionEnd ?? start;
      const next = current.slice(0, start) + e.key + current.slice(end);
      // Сравниваем нечувствительно к регистру.
      if (!expectedRaw.toLowerCase().startsWith(next.toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onPaste = (e: ClipboardEvent) => {
      if (!expectedRaw) return;
      const t = e.target as HTMLInputElement | HTMLTextAreaElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return;
      const pasted = e.clipboardData?.getData('text') || '';
      const current = t.value;
      const start = t.selectionStart ?? current.length;
      const end = t.selectionEnd ?? start;
      const next = current.slice(0, start) + pasted + current.slice(end);
      if (!expectedRaw.toLowerCase().startsWith(next.toLowerCase())) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    doc.addEventListener('keydown', onKeyDown, true);
    doc.addEventListener('paste', onPaste, true);

    // Auto-focus first input inside the hotspot box — оператор сразу может
    // печатать без обязательного клика. Особенно важно когда hotspot
    // визуально не совсем точно совпадает с инпутом.
    try {
      const cx = bx + bw / 2;
      const cy = by + bh / 2;
      const probes: Array<[number, number]> = [
        [cx, cy],
        [bx + 12, cy], [bx + bw - 12, cy],
        [cx, by + 12], [cx, by + bh - 12],
      ];
      for (const [px, py] of probes) {
        const el = doc.elementFromPoint(px, py) as HTMLElement | null;
        if (!el) continue;
        let curr: HTMLElement | null = el;
        let found: HTMLElement | null = null;
        for (let hop = 0; hop < 4 && curr && curr !== doc.body; hop++) {
          if (curr.tagName === 'INPUT' || curr.tagName === 'TEXTAREA' || curr.isContentEditable) {
            found = curr;
            break;
          }
          curr = curr.parentElement;
        }
        if (found) {
          try { (found as HTMLInputElement).focus(); } catch {}
          break;
        }
      }
    } catch {}

    return () => {
      doc.removeEventListener('click', onClick, true);
      doc.removeEventListener('input', onInput, true);
      doc.removeEventListener('keydown', onKeyDown, true);
      doc.removeEventListener('paste', onPaste, true);
    };
  }, [waitingHotspot]);

  const startStage = useCallback(() => {
    clearTimer();
    setActionIdx(0);
    setCursor({ x: initialCursorPos.x, y: initialCursorPos.y, clicking: false, transitionMs: 0 });
    if (stage.actions.length > 0) {
      setPhase('animating');
    } else {
      setPhase('interactive');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const runAction = useCallback(
    (action: TrainingDemoAction, onDone: () => void, nextAction?: TrainingDemoAction | null) => {
      // Авторы стейджей расширили action-схему типами `comment`,
      // `highlight`, `hotspot` (data в БД уже использует их). TypeScript
      // type ещё не обновлён — поэтому as any для доступа к новым полям.
      const a: any = action;
      if (action.type === 'move') {
        const [x, y] = action.to;
        const duration = action.duration ?? DEFAULT_MOVE_MS;
        setCursor((c) => ({ ...c, x, y, clicking: false, transitionMs: duration }));
        timerRef.current = setTimeout(onDone, duration + 50);
      } else if (a.type === 'comment') {
        // Comment — НЕ блокирующий: висит `duration` мс параллельно
        // следующим action'ам (в т.ч. hotspot'у с waitForUser). onDone
        // вызывается СРАЗУ, чтобы плеер продолжил.
        setActiveComment({ at: a.at, text: a.text });
        const duration = a.duration ?? 4000;
        const sig = a.text;
        setTimeout(() => {
          setActiveComment((curr) => (curr && curr.text === sig ? null : curr));
        }, duration);
        onDone();
      } else if (a.type === 'highlight') {
        // Highlight — тоже не блокирующий, висит параллельно.
        setActiveHighlight({ box: a.box });
        const duration = a.duration ?? 2000;
        const sigBox = a.box;
        setTimeout(() => {
          setActiveHighlight((curr) =>
            curr && curr.box[0] === sigBox[0] && curr.box[1] === sigBox[1] ? null : curr,
          );
        }, duration);
        onDone();
      } else if (a.type === 'zoom') {
        // Zoom — наезд камеры на область box. Не блокирующий: следующая
        // action стартует сразу, и cursor/click/comment рисуются ВНУТРИ
        // зум-преобразования (мы крутим transform на родителе iframe'а).
        setActiveZoom({ box: a.box });
        const duration = a.duration ?? 3000;
        const sigBox = a.box;
        setTimeout(() => {
          setActiveZoom((curr) =>
            curr && curr.box[0] === sigBox[0] && curr.box[1] === sigBox[1] ? null : curr,
          );
        }, duration);
        onDone();
      } else if (a.type === 'hotspot') {
        // Инлайновая зона ожидания. Если `waitForUser: true` — плеер ЖДЁТ
        // пока пользователь сам не кликнет в зону / введёт inputText.
        // Иначе просто показываем подсветку на duration.
        const waitForUser = a.waitForUser === true;
        setWaitingHotspot({ box: a.box, expectedInput: a.inputText });
        if (waitForUser) {
          // onDone отложен — вызовется в effect'е, отлавливающем клик/ввод
          pendingHotspotResolveRef.current = () => {
            setWaitingHotspot(null);
            pendingHotspotResolveRef.current = null;
            onDone();
          };
        } else {
          const duration = a.duration ?? 2000;
          timerRef.current = setTimeout(() => {
            setWaitingHotspot(null);
            onDone();
          }, duration);
        }
      } else if (action.type === 'click') {
        const [x, y] = action.at;
        setCursor((c) => ({ ...c, x, y, clicking: true, transitionMs: 400 }));
        // After cursor reaches the target — dispatch a real click into the demo,
        // then continue to the next action.
        timerRef.current = setTimeout(() => {
          if (iframeRef.current) {
            dispatchClickIntoIframe(iframeRef.current, x, y);
          }
          setCursor((c) => ({ ...c, clicking: false }));
          onDone();
        }, 500);
      } else if (action.type === 'wait') {
        timerRef.current = setTimeout(onDone, action.duration);
      } else if (action.type === 'type') {
        const [x, y] = action.at;
        const duration = 400;
        console.log('[training] TYPE action:', action.text, 'at', x, y);
        setCursor((c) => ({ ...c, x, y, clicking: false, transitionMs: duration }));

        const inputEl = findInputInIframe(iframeRef.current, x, y);
        if (inputEl) {
          try { (inputEl as HTMLInputElement).focus(); } catch {}
        } else {
          console.log('[training] no input found at', x, y);
        }

        const perChar = 90;
        let shown = 0;
        const tick = () => {
          shown += 1;
          if (inputEl) {
            const partial = action.text.slice(0, shown);
            setNativeInputValue(inputEl, partial);
          }
          if (shown < action.text.length) {
            timerRef.current = setTimeout(tick, perChar);
          } else {
            // Smart-clear: если следующий action — это hotspot с
            // waitForUser+inputText (юзер должен напечатать сам), очищаем
            // поле, чтобы он не дописывал к нашему примеру и не путался.
            // Если же следом идёт ещё анимация (move/click/другой type) —
            // оставляем текст, чтобы был виден контекст.
            const nA: any = nextAction;
            const isUserInputNext =
              nA &&
              nA.type === 'hotspot' &&
              nA.waitForUser === true &&
              typeof nA.inputText === 'string' &&
              nA.inputText.trim().length > 0;
            timerRef.current = setTimeout(() => {
              if (isUserInputNext && inputEl) {
                try { setNativeInputValue(inputEl, ''); } catch {}
              }
              onDone();
            }, 1000);
          }
        };
        timerRef.current = setTimeout(tick, duration + 200);
      } else if (a.type === 'drag') {
        // Drag gesture: курсор плавно ползёт from→to. Dispatch strategy:
        //   • pointerdown / mousedown / dragstart  → на startEl
        //   • pointermove / mousemove              → на document (drag-
        //     библиотеки react-dnd/dnd-kit/framer слушают window после mousedown)
        //   • dragover/enter/leave                 → на element-under-cursor
        //   • drop / pointerup / mouseup           → на endEl и document
        //   • dragend                              → на startEl
        const [fx, fy] = a.from;
        const [tx, ty] = a.to;
        const duration = a.duration ?? 1000;
        const STEPS = 24;
        setCursor({ x: fx, y: fy, clicking: true, transitionMs: 0 });
        const iframe = iframeRef.current;
        const doc = iframe?.contentDocument || null;
        const win = iframe?.contentWindow as any;
        const startEl = doc?.elementFromPoint(fx, fy) as HTMLElement | null;
        let dataTransfer: any = null;
        try { dataTransfer = new (win.DataTransfer || DataTransfer)(); } catch {}

        const fireMouse = (type: string, target: EventTarget | null, x: number, y: number, buttons: number) => {
          if (!target || !win) return;
          try {
            const ev = new (win.MouseEvent || MouseEvent)(type, {
              bubbles: true, cancelable: true, view: win,
              clientX: x, clientY: y, button: 0, buttons,
            });
            target.dispatchEvent(ev);
          } catch {}
        };
        const firePointer = (type: string, target: EventTarget | null, x: number, y: number, buttons: number) => {
          if (!target || !win) return;
          try {
            const PE = win.PointerEvent || (window as any).PointerEvent;
            if (!PE) return;
            const pev = new PE(type, {
              bubbles: true, cancelable: true,
              clientX: x, clientY: y,
              pointerId: 9999, pointerType: 'mouse', isPrimary: true,
              button: 0, buttons,
            });
            target.dispatchEvent(pev);
          } catch {}
        };
        const fireDrag = (type: string, target: EventTarget | null, x: number, y: number) => {
          if (!target || !win) return;
          try {
            const DE = win.DragEvent || (window as any).DragEvent;
            if (!DE) return;
            const dev = new DE(type, {
              bubbles: true, cancelable: true,
              clientX: x, clientY: y,
              dataTransfer: dataTransfer || undefined,
            });
            target.dispatchEvent(dev);
          } catch {}
        };

        firePointer('pointerdown', startEl, fx, fy, 1);
        fireMouse('mousedown', startEl, fx, fy, 1);
        // Не диспатчим dragstart и не вызываем setPointerCapture — они
        // триггерили onDragStart на cards с draggable=true и открывали
        // «select PC» диалог в начале drag'а.

        // Сохраняем helper-параметры для booking grid — вызовем helper
        // СРАЗУ в финальном tick'е, когда визуальная анимация курсора
        // достигнет to-точки. Не через setTimeout — иначе ощущается
        // «лишняя пауза перед открытием диалога».
        let bookingHelperFn: (() => void) | null = null;
        try {
          const helper = (win as any)?.__training?.selectBookingRange;
          const endEl = doc?.elementFromPoint(tx, ty) as HTMLElement | null;
          const startCell = startEl?.closest?.('[data-training-cell]') as HTMLElement | null;
          const endCell = endEl?.closest?.('[data-training-cell]') as HTMLElement | null;
          if (helper && startCell && endCell) {
            const computer = startCell.getAttribute('data-computer');
            const startTime = startCell.getAttribute('data-slot-time');
            const endTime = endCell.getAttribute('data-slot-time');
            if (computer && startTime && endTime) {
              bookingHelperFn = () => { try { helper(computer, startTime, endTime); } catch {} };
            }
          }
        } catch {}

        let step = 0;
        let lastOverEl: HTMLElement | null = startEl;
        const tick = () => {
          step += 1;
          const t = step / STEPS;
          const nx = Math.round(fx + (tx - fx) * t);
          const ny = Math.round(fy + (ty - fy) * t);
          setCursor({ x: nx, y: ny, clicking: true, transitionMs: duration / STEPS });

          // Dispatch на ВСЕ возможные таргеты: documentElement, document, window,
          // overEl. Demo может слушать на любом — мы покрываем всё.
          const docElem = doc?.documentElement || null;
          const overEl = doc?.elementFromPoint(nx, ny) as HTMLElement | null;
          firePointer('pointermove', docElem, nx, ny, 1);
          fireMouse('mousemove', docElem, nx, ny, 1);
          firePointer('pointermove', doc, nx, ny, 1);
          fireMouse('mousemove', doc, nx, ny, 1);
          firePointer('pointermove', win, nx, ny, 1);
          fireMouse('mousemove', win, nx, ny, 1);
          if (overEl && overEl !== docElem) {
            firePointer('pointermove', overEl, nx, ny, 1);
            fireMouse('mousemove', overEl, nx, ny, 1);
          }

          if (overEl && overEl !== lastOverEl) {
            // Классические mouse/pointer enter/leave/over/out — grid-selection
            // UIs используют onMouseEnter на каждой ячейке.
            // HTML5 dragenter/leave/over убраны — они триггерят onDragStart
            // на cards с draggable=true и открывают левые диалоги.
            fireMouse('mouseout', lastOverEl, nx, ny, 1);
            fireMouse('mouseleave', lastOverEl, nx, ny, 1);
            fireMouse('mouseover', overEl, nx, ny, 1);
            fireMouse('mouseenter', overEl, nx, ny, 1);
            firePointer('pointerout', lastOverEl, nx, ny, 1);
            firePointer('pointerleave', lastOverEl, nx, ny, 1);
            firePointer('pointerover', overEl, nx, ny, 1);
            firePointer('pointerenter', overEl, nx, ny, 1);
            lastOverEl = overEl;
          }

          if (step < STEPS) {
            timerRef.current = setTimeout(tick, duration / STEPS);
          } else {
            const endEl = doc?.elementFromPoint(tx, ty) as HTMLElement | null;
            // Mouseup на ВСЕ таргеты.
            firePointer('pointerup', docElem, tx, ty, 0);
            fireMouse('mouseup', docElem, tx, ty, 0);
            firePointer('pointerup', doc, tx, ty, 0);
            fireMouse('mouseup', doc, tx, ty, 0);
            firePointer('pointerup', win, tx, ty, 0);
            fireMouse('mouseup', win, tx, ty, 0);
            if (endEl) {
              firePointer('pointerup', endEl, tx, ty, 0);
              fireMouse('mouseup', endEl, tx, ty, 0);
            }
            // Booking helper срабатывает СЕЙЧАС, в том же tick'е что и
            // mouseup — без дополнительного setTimeout. Перекрывает любой
            // диалог открытый из синтетического mouseup и устанавливает
            // правильный range.
            if (bookingHelperFn) bookingHelperFn();
            setCursor((c) => ({ ...c, clicking: false }));
            timerRef.current = setTimeout(onDone, 100);
          }
        };
        // Первый mousemove через 30ms (один animation frame) — react успевает
        // отрендерить isDragging=true перед началом перемещения. Раньше было
        // 200ms — ощущалось как «большая пауза перед началом drag'а».
        timerRef.current = setTimeout(tick, 30);
      } else {
        onDone();
      }
    },
    [],
  );

  useEffect(() => {
    if (phase !== 'animating') return;
    const action = stage.actions[actionIdx];
    if (!action) {
      // Animation finished. If stage has a top-level hotspot — ждём клика
      // юзера в нём (через 'interactive'). Если же у стейджа только инлайновые
      // hotspot-action'ы (которые уже сами зарезолвили) — сразу complete +
      // выходим в 'browse', чтобы появились кнопки «Далее» / «Повторить».
      timerRef.current = setTimeout(() => {
        if (effectiveHotspot) {
          setPhase('interactive');
        } else {
          // onComplete всегда вызываем — родительский handleStageComplete
          // идемпотентен (проверяет completedIds.has() сам). Закрытие могло
          // содержать устаревшее значение alreadyCompleted из-за того что
          // deps useEffect'а не включают этот пропс.
          onComplete();
          setPhase('browse');
        }
      }, RESULT_HOLD_MS);
      return clearTimer;
    }
    const nextAction = stage.actions[actionIdx + 1] ?? null;
    runAction(action, () => setActionIdx((i) => i + 1), nextAction);
    return clearTimer;
  }, [phase, actionIdx, stage.actions, runAction]);

  const handleMissedClick = () => {
    if (phase !== 'interactive') return;
    setHint(true);
    setTimeout(() => setHint(false), 800);
  };

  // Listen for real interactions INSIDE the iframe in interactive phase.
  // Logic:
  //  - if inputText set AND no click required → typing matches advances
  //  - if hotspot click required AND inputText empty → click in hotspot advances
  //  - if BOTH set → must type the expected value (anywhere in iframe) AND click in hotspot
  useEffect(() => {
    if (phase !== 'interactive' || !effectiveHotspot || !iframeRef.current) return;
    const iframe = iframeRef.current;
    const [bx, by, bw, bh] = effectiveHotspot.box;
    const expected = effectiveHotspot.inputText?.trim();
    const requiresInput = !!expected;

    const inHotspot = (x: number, y: number) =>
      x >= bx && x <= bx + bw && y >= by && y <= by + bh;

    const normalize = (s: string) => (s || '').replace(/[^\p{L}\p{N}]/gu, '').toLowerCase();
    const want = expected ? normalize(expected) : '';

    // State across events
    let inputDone = !requiresInput; // if no input expected, consider "done"

    const onIframeInput = (e: Event) => {
      if (!requiresInput) return;
      const target = e.target as HTMLInputElement | HTMLTextAreaElement | null;
      if (!target) return;
      const tag = target.tagName;
      const editable = (target as HTMLElement).isContentEditable;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !editable) return;
      const raw =
        tag === 'INPUT' || tag === 'TEXTAREA'
          ? (target as HTMLInputElement | HTMLTextAreaElement).value
          : (target as HTMLElement).textContent || '';
      const v = normalize(raw);
      if (v && (v === want || v.includes(want))) {
        inputDone = true;
        console.log('[training] input matched — complete');
        onComplete();
        // Держим «успех» RESULT_HOLD_MS — юзер успевает увидеть, что
        // ввод совпал, прежде чем плеер скажет «иди дальше».
        setTimeout(() => setPhase('browse'), RESULT_HOLD_MS);
      } else {
        console.log('[training] input change, value=', raw, 'normalized=', v, 'want=', want);
      }
    };

    const onIframeClick = (e: MouseEvent) => {
      if (requiresInput) return;
      if (!inHotspot(e.clientX, e.clientY)) return;
      console.log('[training] click in hotspot — complete');
      onComplete();
      // Та же пауза, что и для ввода — кнопки появятся через RESULT_HOLD_MS.
      setTimeout(() => setPhase('browse'), RESULT_HOLD_MS);
    };

    let attached = false;
    const tryAttach = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        doc.addEventListener('click', onIframeClick, true);
        doc.addEventListener('input', onIframeInput, true);
        doc.addEventListener('change', onIframeInput, true);
        doc.addEventListener('keyup', onIframeInput, true);
        attached = true;
      } catch (e) {
        console.warn('[training] cannot attach iframe listener:', e);
      }
    };

    tryAttach();
    const detach = () => {
      try {
        iframe.contentDocument?.removeEventListener('click', onIframeClick, true);
        iframe.contentDocument?.removeEventListener('input', onIframeInput, true);
        iframe.contentDocument?.removeEventListener('change', onIframeInput, true);
        iframe.contentDocument?.removeEventListener('keyup', onIframeInput, true);
      } catch {}
    };
    if (!attached) {
      const onLoad = () => tryAttach();
      iframe.addEventListener('load', onLoad);
      return () => {
        iframe.removeEventListener('load', onLoad);
        detach();
      };
    }
    return detach;
  }, [phase, effectiveHotspot, onComplete]);


  // Self-practice: 4 невидимых div-блокатора вокруг кнопки Пополнения.
  //
  // Берём keyword из названия этапа («Шаг 1 - Пополнение» → "Пополнение").
  // Находим leaf-кнопку с этим текстом в iframe. Берём её bounding rect.
  // Прикладываем 4 div'а к doc.body:
  //
  //   ┌──────────────────────┐
  //   │       TOP            │
  //   ├──────┬─────────┬─────┤
  //   │ LEFT │ ПОПОЛН. │RIGHT│
  //   ├──────┴─────────┴─────┤
  //   │      BOTTOM          │
  //   └──────────────────────┘
  //
  // Все клики ВНЕ прямоугольника Пополнения уходят в div-блокаторы,
  // у которых pointer-events:auto + cursor:not-allowed. Они физически
  // перехватывают MouseEvent на уровне браузерного hit-testing'а,
  // ДО любых React-handler'ов демки. Сам Пополнение остаётся «дыркой»,
  // куда клик доходит свободно.
  useEffect(() => {
    if (phase !== 'interactive' || !allowedBoxes || !allowedBoxes.length || !iframeRef.current) return;
    const iframe = iframeRef.current;
    const SEL = 'button, a[href], [role="button"], [role="tab"], [role="menuitem"], [role="link"], [role="option"]';
    // Для БЛОКИРОВКИ берём шире: демка содержит не только кнопки, но и
    // чекбоксы/инпуты («Посадка», «Бронь» на карточках ПК) — без этого
    // они оставались кликабельными вне разрешённой зоны. Поиск target-
    // кнопки по тексту по-прежнему идёт по SEL (кнопки/ссылки).
    const LOCK_SEL =
      SEL +
      ', input, select, textarea, label, [role="checkbox"], [role="switch"], [role="radio"], [role="menuitemcheckbox"]';
    const keyword = (stage.title || '')
      .replace(/^.*?[-—:]\s*/, '')
      .trim()
      .toLowerCase();
    if (!keyword) return;

    let cancelled = false;
    const LOCK_ATTR = 'data-sp-locked';

    // Собираем ВСЕ координаты из actions стейджа — это «разрешённые точки»
    // на которые могут попадать клики юзера. Для каждой кнопки в demo
    // проверим: содержит ли её bounding-rect хоть одну такую точку?
    //   • action.at        (click/comment/type/hotspot)
    //   • action.to        (move)
    //   • action.from      (drag start)
    //   • action.box центр (hotspot/highlight)
    //   • plus action.to у drag (drag end)
    const allowedCenters: Array<[number, number]> = [];
    for (const a of (stage.actions || []) as any[]) {
      if (a.at && Array.isArray(a.at)) allowedCenters.push([a.at[0], a.at[1]]);
      if (a.to && Array.isArray(a.to)) allowedCenters.push([a.to[0], a.to[1]]);
      if (a.from && Array.isArray(a.from)) allowedCenters.push([a.from[0], a.from[1]]);
      if (a.box && Array.isArray(a.box)) {
        const [bx, by, bw, bh] = a.box;
        allowedCenters.push([bx + bw / 2, by + bh / 2]);
      }
    }

    const restoreLocked = (doc: Document) => {
      doc.querySelectorAll<HTMLElement>('[' + LOCK_ATTR + ']').forEach((el) => {
        const prev = el.getAttribute(LOCK_ATTR) || '';
        el.style.pointerEvents = prev;
        el.style.removeProperty('cursor');
        el.removeAttribute(LOCK_ATTR);
      });
    };

    const apply = () => {
      if (cancelled) return;
      let doc: Document | null = null;
      try { doc = iframe.contentDocument; } catch { doc = null; }
      if (!doc || !doc.body) { setTimeout(apply, 100); return; }

      // Текстовая target-кнопка (по keyword) — добавляем её центр как
      // ещё одну allowed-точку, на случай если в action'ах нет coords
      // (стейдж без actions, только comments) или actions сильно сдвинулись.
      const wantStart = keyword + ' ';
      const wantStartN = keyword + '\n';
      const dynamicCenters: Array<[number, number]> = [...allowedCenters];
      const candidates = doc.querySelectorAll<HTMLElement>(SEL);
      for (const btn of Array.from(candidates)) {
        if (btn.querySelector(SEL)) continue;
        const text = (btn.textContent || '').trim().toLowerCase();
        if (!text || text.length > 60) continue;
        if (text === keyword || text.startsWith(wantStart) || text.startsWith(wantStartN)) {
          const r = btn.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) {
            dynamicCenters.push([r.left + r.width / 2, r.top + r.height / 2]);
          }
          break;
        }
      }

      if (dynamicCenters.length === 0) {
        // Нечего разрешать — оставляем демку как есть (не блокируем).
        return;
      }

      // DOM lockdown: для каждой интерактивной кнопки проверяем — есть ли
      // у нас allowed-точка внутри её bounding-rect. Если ДА — кнопка
      // часть сценария, разрешена. Если НЕТ — блокируем pointer-events:none.
      const allButtons = doc.querySelectorAll<HTMLElement>(LOCK_SEL);
      const allowedEls = new Set<HTMLElement>();
      const blockedEls = new Set<HTMLElement>();

      allButtons.forEach((el) => {
        // Наши инжектированные кнопки подэтапов (в сайдбаре демки —
        // box.setAttribute('data-training-substeps', '1') выше)
        // всегда разрешены. Иначе в self-practice / interactive фазе
        // юзер не может переключаться по подэтапам — на скрине ровно
        // это «не кликается подэтапы в сайдбаре».
        if (el.closest('[data-training-substeps]')) {
          allowedEls.add(el);
          return;
        }
        const r = el.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) return;
        const hit = dynamicCenters.some(([cx, cy]) =>
          cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom
        );
        if (hit) {
          allowedEls.add(el);
          // Также allowedEls для всех потомков — они тоже разрешены
        } else {
          blockedEls.add(el);
        }
      });

      // Allowed-кнопки могут быть вложены в blocked-контейнеры (или наоборот).
      // Снимаем блок с allowedEls если у них он был. Применяем блок к blockedEls
      // если ещё не применён.
      allowedEls.forEach((el) => {
        if (el.hasAttribute(LOCK_ATTR)) {
          const prev = el.getAttribute(LOCK_ATTR) || '';
          el.style.pointerEvents = prev;
          el.style.removeProperty('cursor');
          el.removeAttribute(LOCK_ATTR);
        }
      });
      blockedEls.forEach((el) => {
        // Не блокируем если это потомок allowed-элемента (allowedEls обрабатывает его выше)
        let isInsideAllowed = false;
        for (const a of allowedEls) {
          if (a !== el && a.contains(el)) { isInsideAllowed = true; break; }
        }
        if (isInsideAllowed) return;
        if (!el.hasAttribute(LOCK_ATTR)) {
          el.setAttribute(LOCK_ATTR, el.style.pointerEvents || '');
          el.style.setProperty('pointer-events', 'none', 'important');
          el.style.setProperty('cursor', 'not-allowed', 'important');
        }
      });
    };

    apply();

    let obs: MutationObserver | null = null;
    let observedDoc: Document | null = null;
    const ensureObserver = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc || !doc.body) return;
        if (observedDoc === doc && obs) return;
        try { obs?.disconnect(); } catch {}
        // childList+subtree — реагируем когда demo добавляет/убирает кнопки.
        // attribute-мутации (наши собственные style-setProperty) не отслеживаем,
        // чтобы не было loop'а.
        obs = new MutationObserver(() => apply());
        obs.observe(doc.body, { childList: true, subtree: true });
        observedDoc = doc;
      } catch {}
    };
    ensureObserver();
    const onIframeLoad = () => {
      ensureObserver();
      apply();
      [50, 150, 300, 500, 800, 1200, 1800].forEach((d) =>
        setTimeout(() => { if (!cancelled) apply(); }, d)
      );
    };
    iframe.addEventListener('load', onIframeLoad);
    const intv = window.setInterval(() => { ensureObserver(); apply(); }, 400);

    return () => {
      cancelled = true;
      window.clearInterval(intv);
      try { iframe.removeEventListener('load', onIframeLoad); } catch {}
      try { obs?.disconnect(); } catch {}
      try {
        const doc = iframe.contentDocument;
        if (doc) restoreLocked(doc);
      } catch {}
    };
  }, [phase, allowedBoxes, stage.title, stage.actions]);

  // Скролл внутри демки в обучении НЕ нужен: показываем весь natural-кадр,
  // отмасштабированный под вьюпорт. Внутренний скролл iframe (когда контент
  // демки выше кадра) — артефакт, мешает и сбивает прицел подсветок. Глушим
  // overflow прямо внутри iframe (демка same-origin). Если этап ЯВНО
  // размечен allowScroll — не трогаем (на случай сценариев, где скролл
  // сам по себе часть обучения).
  useEffect(() => {
    if ((stage as any).allowScroll) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    let cancelled = false;
    const STYLE_ID = 'tr-noscroll-style';
    const apply = () => {
      if (cancelled) return;
      let doc: Document | null = null;
      try { doc = iframe.contentDocument; } catch { doc = null; }
      if (!doc || !doc.head) { window.setTimeout(apply, 120); return; }
      if (!doc.getElementById(STYLE_ID)) {
        const st = doc.createElement('style');
        st.id = STYLE_ID;
        st.textContent = 'html,body{overflow:hidden!important;}';
        doc.head.appendChild(st);
      }
    };
    apply();
    const onLoad = () => apply();
    iframe.addEventListener('load', onLoad);
    const intv = window.setInterval(apply, 600);
    return () => {
      cancelled = true;
      window.clearInterval(intv);
      try { iframe.removeEventListener('load', onLoad); } catch {}
      try { iframe.contentDocument?.getElementById(STYLE_ID)?.remove(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Self-practice: detect when the user clicks the LAST scripted action's
  // button — that's the natural «закончил подэтап» moment. Without this the
  // user runs through all the steps, the demo shows its success toast,
  // but the training stage stays in 'interactive' and never auto-completes.
  // We wait ~1.5s after the click (so the toast/animation plays) before
  // firing onComplete and exiting back to browse-phase.
  useEffect(() => {
    if (phase !== 'interactive' || !allowedBoxes || !allowedBoxes.length || !iframeRef.current) return;
    const iframe = iframeRef.current;

    // Find the last action that actually represents a click target.
    // comment/highlight — purely visual hints, not interaction points.
    const interactiveTypes = new Set(['click', 'type', 'hotspot', 'move']);
    let finalPoint: { x: number; y: number } | null = null;
    for (let i = (stage.actions || []).length - 1; i >= 0; i--) {
      const a = (stage.actions as any[])[i];
      if (!interactiveTypes.has(a?.type)) continue;
      if (a.at && Array.isArray(a.at)) {
        finalPoint = { x: a.at[0], y: a.at[1] };
        break;
      }
      if (a.box && Array.isArray(a.box)) {
        const [bx, by, bw, bh] = a.box;
        finalPoint = { x: bx + bw / 2, y: by + bh / 2 };
        break;
      }
    }
    if (!finalPoint) return;

    let completed = false;
    let pending: ReturnType<typeof setTimeout> | null = null;

    const onIframeClick = (e: MouseEvent) => {
      if (completed) return;
      // Coordinates inside iframe.contentDocument are in the demo's native
      // 1920×1080 viewport — same coordinate space as action.at saved in DB.
      const dx = e.clientX - finalPoint!.x;
      const dy = e.clientY - finalPoint!.y;
      if (Math.sqrt(dx * dx + dy * dy) > 60) return;
      completed = true;
      console.log('[training] self-practice: last action clicked → complete in', RESULT_HOLD_MS, 'ms');
      pending = setTimeout(() => {
        onComplete();
        setAllowedBoxes(null);
        setPhase('browse');
      }, RESULT_HOLD_MS);
    };

    let attached = false;
    const attach = () => {
      if (attached) return;
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        doc.addEventListener('click', onIframeClick, true);
        attached = true;
      } catch {
        // cross-origin or not ready — retry on iframe load
      }
    };
    attach();
    const onLoad = () => attach();
    iframe.addEventListener('load', onLoad);

    return () => {
      if (pending) clearTimeout(pending);
      try { iframe.removeEventListener('load', onLoad); } catch {}
      try {
        iframe.contentDocument?.removeEventListener('click', onIframeClick, true);
      } catch {}
    };
  }, [phase, allowedBoxes, stage.actions, onComplete]);

  // Прячем бейджик «Нет связи», который рисует сама демо-админка
  // (offline-индикатор её API). В обучении он лишний и сбивает —
  // источник в чужом репозитории, поэтому правим визуально на лету.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    let cancelled = false;
    let obs: MutationObserver | null = null;
    let observedDoc: Document | null = null;

    const hideOfflineBadge = (doc: Document) => {
      // Берём только конечные текстовые узлы — без рекурсии на
      // потомков. Поднимаемся до 2 уровней вверх к компактному
      // контейнеру, чтобы скрыть весь тег целиком, а не только текст.
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
      const victims: HTMLElement[] = [];
      let node = walker.nextNode() as HTMLElement | null;
      while (node) {
        if (
          node.children.length === 0 &&
          (node.textContent || '').trim() === 'Нет связи'
        ) {
          let target: HTMLElement = node;
          for (let i = 0; i < 2 && target.parentElement; i++) {
            const p = target.parentElement;
            if ((p.textContent || '').trim().length < 30) target = p;
            else break;
          }
          victims.push(target);
        }
        node = walker.nextNode() as HTMLElement | null;
      }
      victims.forEach((el) => {
        if (el.style.display !== 'none') el.style.display = 'none';
      });
    };

    const apply = () => {
      if (cancelled) return;
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) return;
        hideOfflineBadge(doc);
        if (observedDoc !== doc) {
          try { obs?.disconnect(); } catch {}
          obs = new MutationObserver(() => hideOfflineBadge(doc));
          obs.observe(doc.body, { childList: true, subtree: true, characterData: true });
          observedDoc = doc;
        }
      } catch {
        // cross-origin или ещё не загружен — ничего страшного.
      }
    };

    apply();
    const onLoad = () => apply();
    iframe.addEventListener('load', onLoad);
    const intv = window.setInterval(apply, 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intv);
      try { iframe.removeEventListener('load', onLoad); } catch {}
      try { obs?.disconnect(); } catch {}
    };
  }, []);

  // Авто-раскрытие активной категории сайдбара демки + плавная анимация
  // сворачивания. Демка построена на Radix/shadcn-стиле — collapsible-блоки
  // имеют [data-state="open"|"closed"], активный пункт — [data-state="active"].
  //
  // Без этого фикса пункт «Главная» в боковом меню демки рендерится свернутым,
  // и юзеру нужно один-два раза кликнуть, чтобы вылезли подэтапы — баг,
  // на который пользователь и жаловался. Мы со стороны support-crm:
  //   1) инжектим CSS-transition на data-state — раскрытие/сворачивание
  //      идёт плавно, без дёрганья
  //   2) находим текущий активный пункт и заставляем его родительский
  //      collapsible встать в data-state="open"
  // Re-применяем на mount, load iframe, смену этапа и через MutationObserver
  // (Radix постоянно перетряхивает aria-state'ы).
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    let cancelled = false;
    let obs: MutationObserver | null = null;
    let observedDoc: Document | null = null;

    const STYLE_ID = 'cc-training-submenu-fix';

    const injectStyle = (doc: Document) => {
      if (doc.getElementById(STYLE_ID)) return;
      const s = doc.createElement('style');
      s.id = STYLE_ID;
      // Плавный transition на любых элементах с data-state (collapsible'ы
      // Radix используют data-state для open/closed) и на собственно
      // [data-state] контейнерах сайдбара.
      s.textContent = `
        [data-state] { transition: max-height 0.28s ease, opacity 0.22s ease; }
        [data-state="closed"] { overflow: hidden; }
      `;
      (doc.head || doc.body)?.appendChild(s);
    };

    const expandActive = (doc: Document) => {
      // Любой активный nav-item — раскрываем ближайший родительский
      // collapsible. data-state=active И/ИЛИ aria-current=page И/ИЛИ
      // data-active=true — три популярных конвенции, целим во все.
      const actives = doc.querySelectorAll(
        '[data-state="active"], [aria-current="page"], [data-active="true"]',
      );
      actives.forEach((el) => {
        let p: HTMLElement | null = el.parentElement;
        let hops = 0;
        while (p && hops < 6) {
          if (p.getAttribute && p.getAttribute('data-state') === 'closed') {
            p.setAttribute('data-state', 'open');
            return;
          }
          p = p.parentElement;
          hops++;
        }
      });
    };

    const apply = () => {
      if (cancelled) return;
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) return;
        injectStyle(doc);
        expandActive(doc);
        if (observedDoc !== doc) {
          try { obs?.disconnect(); } catch {}
          obs = new MutationObserver(() => expandActive(doc));
          obs.observe(doc.body, { attributes: true, subtree: true, attributeFilter: ['data-state', 'aria-current', 'data-active'] });
          observedDoc = doc;
        }
      } catch {
        // cross-origin or not ready — ignore
      }
    };

    apply();
    const onLoad = () => apply();
    iframe.addEventListener('load', onLoad);
    const intv = window.setInterval(apply, 800);

    return () => {
      cancelled = true;
      window.clearInterval(intv);
      try { iframe.removeEventListener('load', onLoad); } catch {}
      try { obs?.disconnect(); } catch {}
    };
  }, [stage.id]);


  // stage.comments slideshow: rotate one-by-one during animating только.
  // ВАЖНО:
  //  • Пока waitingHotspot активен (плеер ЖДЁТ пользователя — ввести текст,
  //    нажать в зону) — ротация ПАУЗИТСЯ. Иначе пока юзер думает, комменты
  //    прокручиваются дальше и идут по кругу — это раздражает.
  //  • НЕ зацикливаем — доходим до последнего и остаёмся на нём.
  //  • Сброс на 0 на phase change / stage change.
  useEffect(() => {
    if (phase !== 'animating') {
      setStageCommentIdx(0);
      return;
    }
    const comments = (stage as any).comments;
    if (!Array.isArray(comments) || comments.length <= 1) return;
    // Если waitingHotspot активен — НЕ запускаем интервал, комментарий
    // зафиксирован на текущем (юзеру читать/действовать).
    if (waitingHotspot) return;
    const intv = window.setInterval(() => {
      setStageCommentIdx((i) => Math.min(i + 1, comments.length - 1));
    }, 5000);
    return () => window.clearInterval(intv);
  }, [phase, stage.id, waitingHotspot]);


  // Auto-start when stage is shown
  useEffect(() => {
    if (phase === 'idle' && !alreadyCompleted) {
      // small delay so UI renders first
      timerRef.current = setTimeout(startStage, 600);
    }
    return clearTimer;
  }, [phase, alreadyCompleted, startStage]);

  const showSpotlight = phase === 'interactive' && !!effectiveHotspot;

  return (
    <div
      className={
        fillHeight
          ? 'rounded-2xl overflow-hidden flex flex-col flex-1 min-h-0'
          : 'rounded-2xl overflow-hidden'
      }
    >
      {(() => {
        const showRepeat = phase !== 'animating' && phase !== 'idle';
        const showNext = alreadyCompleted && canProceed;
        if ((!showRepeat && !showNext) || !ctrlSlot) return null;
        // Кнопки управления порталим в строку-крошку (один ряд со всеми
        // контролами) — отдельный ряд-тулбар больше не занимает вертикаль,
        // демо-плеер за счёт этого крупнее.
        return createPortal(
          <>
            {showRepeat && (
              <Button variant="secondary" size="sm" icon={<Play className="h-4 w-4" />} onClick={startStage}>
                Повторить
              </Button>
            )}
            {showNext && (
              <Button variant="primary" size="sm" icon={<ArrowRight className="h-4 w-4" />} onClick={onNext}>
                Далее
              </Button>
            )}
          </>,
          ctrlSlot,
        );
      })()}

      <div
        className={`relative flex items-center justify-center select-none ${
          fillHeight ? 'flex-1 min-h-0' : ''
        }`}
        onClick={handleMissedClick}
      >
        <ScaledIframe
          src={normalizeIframeUrl(applyVariantPrefix(stage.iframeUrl, variant))}
          title={stage.title}
          naturalW={imgSize.w}
          naturalH={imgSize.h}
          // Iframe должен быть кликабельным когда плеер ЖДЁТ пользователя
          // (waitingHotspot.waitForUser=true) — иначе невозможно нажать
          // подсвеченную кнопку или ввести данные в подсвеченный инпут.
          interactive={(phase !== 'animating' && phase !== 'idle') || !!waitingHotspot}
          iframeRef={iframeRef}
          zoom={activeZoom}
        >
          {/* Animated cursor (during animation phase) */}
          {phase === 'animating' && (
            <div
              className="absolute pointer-events-none z-30"
              style={{
                left: `${(cursor.x / imgSize.w) * 100}%`,
                top: `${(cursor.y / imgSize.h) * 100}%`,
                transform: 'translate(-4px, -2px)',
                transition: `left ${cursor.transitionMs}ms cubic-bezier(0.25, 0.1, 0.25, 1), top ${cursor.transitionMs}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }}>
                <path d="M5 3L19 12L12 13L9 20L5 3Z" fill="white" stroke="black" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              {cursor.clicking && (
                <span
                  className="absolute rounded-full"
                  style={{
                    left: 0, top: 0,
                    width: 18, height: 18,
                    transform: 'translate(-9px, -9px)',
                    background: 'rgba(99, 102, 241, 0.25)',
                    border: '1.5px solid rgba(99, 102, 241, 0.9)',
                    boxShadow: '0 0 0 2px rgba(99,102,241,0.15)',
                    animation: 'training-ripple 1.4s ease-in-out infinite',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          )}

          {/* Inline ACTION overlays — для типов comment/highlight/hotspot
              которые редактор добавляет в actions[] и которые плеер теперь
              реально отрисовывает (раньше эти кадры пропускались). */}
          {activeComment && (
            <div
              className="absolute z-30 pointer-events-none"
              style={{
                left: `${(activeComment.at[0] / imgSize.w) * 100}%`,
                top: `${(activeComment.at[1] / imgSize.h) * 100}%`,
                // Same anchor as editor: bottom-left of the bubble sits at the
                // comment point, with a small 8px left shift. Earlier we used
                // translate(-50%, -50%) which centered the bubble on the point
                // and caused it to drift off to the side compared to how the
                // operator placed it in the editor preview.
                transform: 'translate(-8px, -100%)',
                maxWidth: 360,
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                  color: '#ffffff',
                  padding: '10px 14px',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 500,
                  lineHeight: 1.4,
                  boxShadow: '0 8px 22px rgba(124, 58, 237, 0.4)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  whiteSpace: 'pre-wrap',
                  letterSpacing: 0.1,
                  animation: 'training-fade-in 0.2s ease-out',
                }}
              >
                {activeComment.text}
              </div>
            </div>
          )}
          {activeHighlight && (() => {
            // Спотлайт: затемняем ВСЁ вне выделенной области (SVG-маска с
            // «дыркой» по рамке), а саму рамку обводим аккуратной бесцветной
            // (белой полупрозрачной) линией — без жёлтого пунктира.
            const [hx, hy, hw, hh] = activeHighlight.box;
            return (
              <svg
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 28,
                }}
                viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
                preserveAspectRatio="none"
              >
                <defs>
                  <mask id="training-highlight-spot">
                    <rect x={0} y={0} width={imgSize.w} height={imgSize.h} fill="white" />
                    <rect x={hx} y={hy} width={hw} height={hh} rx={8} fill="black" />
                  </mask>
                </defs>
                {/* затемнение всего, кроме выделенной области */}
                <rect
                  x={0}
                  y={0}
                  width={imgSize.w}
                  height={imgSize.h}
                  fill="rgba(0,0,0,0.6)"
                  mask="url(#training-highlight-spot)"
                />
                {/* бесцветная аккуратная обводка рамки */}
                <rect
                  x={hx}
                  y={hy}
                  width={hw}
                  height={hh}
                  rx={8}
                  fill="none"
                  stroke="rgba(255,255,255,0.55)"
                  strokeWidth={2}
                />
              </svg>
            );
          })()}
          {waitingHotspot && !waitingHotspot.expectedInput && (() => {
            // Рамку показываем только когда hotspot — это «кликни сюда»
            // (нет expectedInput). При вводе текста рамка визуально мешает,
            // оператор и так видит подсвеченный фокус инпута + комментарий.
            const [x, y, w, h] = snappedBox || waitingHotspot.box;
            return (
              <svg
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 29 }}
                viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
                preserveAspectRatio="none"
              >
                <rect
                  x={x} y={y} width={w} height={h}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={4}
                />
              </svg>
            );
          })()}

          {/* 4 невидимых блокатора вокруг waitingHotspot — строгий
              сценарий. Юзер может кликать ТОЛЬКО зону hotspot'а, всё
              остальное мёртвое. Раньше я их убирал из-за Картой/Наличными
              в корзине, но user явно попросил: «сделай чтобы я не мог
              ничего другое жать при прохождении этапов». Если автор
              сценария хочет дать выбор — пусть нарисует hotspot.box
              шире, чтобы покрывал оба варианта. */}
          {waitingHotspot && (() => {
            const [bx, by, bw, bh] = snappedBox || waitingHotspot.box;
            const PAD = 6;
            const xp = ((bx - PAD) / imgSize.w) * 100;
            const yp = ((by - PAD) / imgSize.h) * 100;
            const wp = ((bw + PAD * 2) / imgSize.w) * 100;
            const hp = ((bh + PAD * 2) / imgSize.h) * 100;
            const blk: React.CSSProperties = {
              position: 'absolute',
              background: 'transparent',
              pointerEvents: 'auto',
              cursor: 'not-allowed',
              zIndex: 28,
            };
            return (
              <>
                <div style={{ ...blk, left: 0, top: 0, right: 0, height: `${Math.max(0, yp)}%` }} />
                <div style={{ ...blk, left: 0, top: `${yp + hp}%`, right: 0, bottom: 0 }} />
                <div style={{ ...blk, left: 0, top: `${yp}%`, width: `${Math.max(0, xp)}%`, height: `${hp}%` }} />
                <div style={{ ...blk, left: `${xp + wp}%`, top: `${yp}%`, right: 0, height: `${hp}%` }} />
              </>
            );
          })()}

          {/* Comment bubbles: ОДИН активный за раз, только во время animating-
              фазы. Slideshow ротация — каждые 5 сек переключается на
              следующий (см. useEffect выше). На browse/success/interactive
              комментарии скрыты — иначе все висят на success-экране. */}
          {phase === 'animating' && Array.isArray((stage as any).comments) && (stage as any).comments[stageCommentIdx] && (() => {
            const c = (stage as any).comments[stageCommentIdx];
            return (
              <div
                className="absolute pointer-events-none z-40"
                style={{
                  left: `${(c.x / imgSize.w) * 100}%`,
                  top: `${(c.y / imgSize.h) * 100}%`,
                  transform: 'translate(-8px, -100%)',
                }}
              >
                <div
                  className="relative"
                  style={{
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                    color: '#ffffff',
                    padding: '10px 14px',
                    borderRadius: 12,
                    fontSize: 13,
                    fontWeight: 500,
                    maxWidth: 320,
                    boxShadow: '0 8px 22px rgba(124, 58, 237, 0.4)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.4,
                    letterSpacing: 0.1,
                  }}
                >
                  {c.text}
                </div>
              </div>
            );
          })()}

          {/* Spotlight overlay during interactive phase */}
          {showSpotlight && effectiveHotspot && (
            <SpotlightOverlay
              hotspot={effectiveHotspot}
              imgSize={imgSize}
              hint={hint}
              onMissClick={() => {
                setHint(true);
                setTimeout(() => setHint(false), 800);
              }}
            />
          )}


          {/* Self-practice блокирует ненужные кнопки на уровне DOM iframe'а
              (см. useEffect на phase+allowedBoxes выше). Visual overlay'ев
              здесь больше нет — pointer-events:none ставится прямо на
              элементы внутри демки. */}


          {/* Browse phase: dim everything except sidebar + center buttons.
              Если стейдж УЖЕ пройден — вместо «Запустить» показываем выбор:
              «Перейти к следующему» и «Пройти самостоятельно». */}
          {phase === 'browse' && sidebarBounds && (
            <BrowseOverlay
              sidebarBounds={sidebarBounds}
              imgSize={imgSize}
              stageTitle={stage.title}
              alreadyCompleted={alreadyCompleted}
              canProceed={canProceed}
              onStart={startStage}
              onNext={onNext}
              onSelfPractice={() => {
                // Только ТОЧКИ реального взаимодействия — никаких больших
                // highlight'ов «вся панель пополнения» (они захватывают
                // соседние табы вроде Бар/Расход).
                //   • click/type/move + at  →  ±25px вокруг at
                //   • hotspot + at          →  ±25px вокруг at
                //   • hotspot + box (без at)→  сам box (это инпут/кнопка)
                //   • highlight, comment    →  ПРОПУСКАЕМ (только визуал)
                const boxes: Array<[number, number, number, number]> = [];
                const PAD = 25;
                for (const a of stage.actions as any[]) {
                  if (a.type === 'click' || a.type === 'type' || a.type === 'move') {
                    if (a.at) boxes.push([a.at[0] - PAD, a.at[1] - PAD, PAD * 2, PAD * 2]);
                  } else if (a.type === 'hotspot') {
                    if (a.at) {
                      boxes.push([a.at[0] - PAD, a.at[1] - PAD, PAD * 2, PAD * 2]);
                    } else if (a.box) {
                      boxes.push([a.box[0], a.box[1], a.box[2], a.box[3]]);
                    }
                  }
                  // highlight / comment — визуал, не добавляем
                }
                // Раньше делал cache-buster reset iframe.src — нужно было
                // когда блокаторы шли по координатам action.at и iframe
                // должен был быть в START-состоянии. Теперь блокаторы
                // находят кнопку по ТЕКСТУ (keyword из stage.title),
                // состояние демки не важно, и reset не нужен. Reset ещё
                // и обнулял iframe в белый экран.
                setAllowedBoxes(boxes.length ? boxes : null);
                setPhase('interactive');
              }}
            />
          )}

        </ScaledIframe>
      </div>

      {phase === 'interactive' && effectiveHotspot && (
        <div className="px-5 py-2.5 bg-yellow-500/10 border-t border-yellow-500/20 text-yellow-200 text-sm flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
          {effectiveHotspot.inputText
            ? `Введите "${effectiveHotspot.inputText}" в подсвеченное поле`
            : 'Нажмите на подсвеченную область'}
        </div>
      )}

      <style>{`
        @keyframes training-ripple {
          0%, 100% { transform: translate(-9px, -9px) scale(1.0); opacity: 0.9; }
          50%      { transform: translate(-9px, -9px) scale(1.25); opacity: 0.5; }
        }
        @keyframes training-blink {
          50% { opacity: 0; }
        }
        @keyframes training-fade-in {
          /* НЕ трогаем translate здесь — позицию задаёт обёртка плашки
             через transform: translate(-8px, -100%). Если внутренний div
             во время анимации применит translate(-50%, -50%), а в конце
             сбросит — плашка прыгнет на полразмера. Анимируем только
             opacity + scale (transform-origin по умолчанию = центр). */
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes training-pulse {
          0%, 100% { box-shadow: 0 0 16px rgba(251,191,36,0.55); border-color: #fbbf24; }
          50%      { box-shadow: 0 0 28px rgba(251,191,36,0.95); border-color: #fde047; }
        }
      `}</style>
    </div>
  );
};

// Rewrite any absolute URL pointing at the demo into our same-origin proxy /demo/...
// This is required because synthetic clicks into iframe.contentDocument only work
// when iframe is same-origin with the parent.
function normalizeIframeUrl(url: string | null | undefined): string {
  if (!url || url === '/') return '/admin-demo/';
  // Replace cross-origin demo paths with same-origin alias
  const mDir = url.match(/^https?:\/\/[^/]+\/(admin-demo-director)\/?(.*)$/i);
  if (mDir) return '/admin-demo-director/' + mDir[2];
  const m = url.match(/^https?:\/\/[^/]+\/(demoadminpanel|demo|admin-demo)\/?(.*)$/i);
  if (m) return '/admin-demo/' + m[2];
  // Already a local demo path of either flavor — keep as-is
  if (ADMIN_PREFIX_RE.test(url) || DIRECTOR_PREFIX_RE.test(url)) return url;
  // Bare path like "/chats" — assume admin route by default
  return '/admin-demo' + (url.startsWith('/') ? url : '/' + url);
}

// =====================================================
// findInputInIframe — locate input/textarea/contenteditable at iframe coords.
// Walks up DOM if elementFromPoint returns a wrapper.
// =====================================================
function findInputInIframe(
  iframe: HTMLIFrameElement | null,
  x: number,
  y: number,
): HTMLElement | null {
  try {
    const doc = iframe?.contentDocument;
    if (!doc) return null;
    const probes: Array<[number, number]> = [
      [x, y], [x - 4, y], [x + 4, y], [x, y - 4], [x, y + 4],
    ];
    for (const [px, py] of probes) {
      const el = doc.elementFromPoint(px, py) as HTMLElement | null;
      if (!el) continue;
      let curr: HTMLElement | null = el;
      while (curr && curr !== doc.body) {
        if (
          curr.tagName === 'INPUT' ||
          curr.tagName === 'TEXTAREA' ||
          curr.isContentEditable
        ) return curr;
        curr = curr.parentElement;
      }
    }
    // Fallback: any focused input in iframe
    const focused = doc.activeElement as HTMLElement | null;
    if (focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA' || focused.isContentEditable)) {
      return focused;
    }
    return null;
  } catch {
    return null;
  }
}

// =====================================================
// setNativeInputValue — change <input>/<textarea> value AND tell React's
// internal _valueTracker so it sees the change. Without the tracker hack,
// React-controlled inputs ignore programmatic value changes.
// =====================================================
function setNativeInputValue(el: HTMLElement, value: string) {
  try {
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') {
      const inputEl = el as HTMLInputElement;
      // Capture the CURRENT value before mutation. Tracker must hold the
      // *previous* value so React's diff sees a change and fires onChange.
      const previousValue = inputEl.value;
      const proto = tag === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
      const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
      if (setter) setter.call(inputEl, value);
      else inputEl.value = value;
      const tracker = (inputEl as any)._valueTracker;
      if (tracker && typeof tracker.setValue === 'function') {
        // Make tracker differ from new value so React detects a change.
        tracker.setValue(previousValue === value ? value + '_' : previousValue);
      }
    } else if (el.isContentEditable) {
      el.textContent = value;
    }
    el.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  } catch (e) {
    console.log('[training] setNativeInputValue failed:', e);
  }
}

// =====================================================
// dispatchClickIntoIframe — fires a real-looking click sequence inside same-origin iframe.
// Tries multiple targets at the spot (center + small offsets) and walks up to find a
// click-handler-bearing ancestor. Sends pointer + mouse events; logs the chosen target.
// =====================================================
function dispatchClickIntoIframe(iframe: HTMLIFrameElement, cx: number, cy: number) {
  try {
    const ifw = iframe.contentWindow as any;
    const doc = iframe.contentDocument;
    if (!doc || !ifw) {
      console.warn('[training] iframe not same-origin or not loaded');
      return;
    }
    // Collect candidates around the click point
    const points = [
      [cx, cy], [cx - 2, cy - 2], [cx + 2, cy + 2],
      [cx, cy - 4], [cx, cy + 4], [cx - 4, cy], [cx + 4, cy],
    ];
    const seen = new Set<Element>();
    const candidates: Element[] = [];
    for (const [x, y] of points) {
      const el = doc.elementFromPoint(x, y);
      if (el && !seen.has(el)) {
        seen.add(el);
        candidates.push(el);
      }
    }
    if (candidates.length === 0) {
      console.warn('[training] no element at hotspot', cx, cy);
      return;
    }
    // Choose best target: prefer button/a/role=button
    let target: Element | null =
      candidates.find((el) => el.closest('button, a, [role="button"], [onclick]'))?.closest('button, a, [role="button"], [onclick]') ||
      candidates[0];

    const baseOpts: any = {
      view: ifw,
      bubbles: true,
      cancelable: true,
      composed: true,
      clientX: cx,
      clientY: cy,
      screenX: cx,
      screenY: cy,
      button: 0,
      buttons: 1,
    };

    const fireSeq = (el: Element) => {
      if (typeof ifw.PointerEvent === 'function') {
        const pOpts = { ...baseOpts, pointerType: 'mouse', pointerId: 1, isPrimary: true };
        el.dispatchEvent(new ifw.PointerEvent('pointerover', pOpts));
        el.dispatchEvent(new ifw.PointerEvent('pointerdown', pOpts));
        el.dispatchEvent(new ifw.PointerEvent('pointerup', { ...pOpts, buttons: 0 }));
      }
      el.dispatchEvent(new ifw.MouseEvent('mousedown', baseOpts));
      el.dispatchEvent(new ifw.MouseEvent('mouseup', { ...baseOpts, buttons: 0 }));
      el.dispatchEvent(new ifw.MouseEvent('click', { ...baseOpts, buttons: 0 }));
      // No native el.click() here — it would fire a second click event and toggle modals shut
    };

    fireSeq(target);
    console.log('[training] click → ', target, 'tag=', target.tagName, 'class=', (target as HTMLElement).className, 'at', cx, cy);
  } catch (e) {
    console.warn('[training] dispatchClickIntoIframe failed:', e);
  }
}

// =====================================================
// ScaledIframe — renders iframe at NATURAL pixel size (matching screenshot
// dimensions where coordinates were picked) and CSS-scales it down to fit
// the parent container. This ensures the demo's responsive layout doesn't
// reflow at smaller widths and hotspot/cursor coords align perfectly.
// =====================================================
const ScaledIframe: React.FC<{
  src: string;
  title: string;
  naturalW: number;
  naturalH: number;
  interactive: boolean;
  iframeRef?: React.RefObject<HTMLIFrameElement>;
  children?: React.ReactNode;
  // Опциональный zoom-наезд: при наличии box (в natural-координатах
  // 1920×1080) внутренний кадр транслируется и масштабируется так, чтобы
  // box занял всё видимое поле, с плавным CSS transition. null = норма.
  zoom?: { box: [number, number, number, number] } | null;
}> = ({ src, title, naturalW, naturalH, interactive, iframeRef, children, zoom }) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  // Lock src on first mount — never reload iframe on subsequent src prop changes.
  // Demo navigation happens internally; we don't want a full reload per stage switch.
  const [lockedSrc] = useState(src);

  useEffect(() => {
    const update = () => {
      if (!outerRef.current) return;
      const containerW = outerRef.current.clientWidth;
      // Простой viewport-based fit (как в editor'е ScaledIframeEditor).
      // НЕ используем parent.clientHeight — он создавал circular dependency
      // между размером iframe и размером родителя, что давало разный
      // визуальный размер контейнера между editor и player → одни и те же
      // box-координаты рисовали рамку разной высоты.
      // Демо тянем максимально по вертикали — над плеером только компактная
      // хлебная крошка (~56px). Раньше вычитали 80 (плюс строка «ШАГИ»),
      // но шаги уехали в боковой сайдбар, так что place освободилось.
      // Вписываем по более жёсткому из двух ограничений (ширина vs высота),
      // чтобы демо было видно ЦЕЛИКОМ — без обрезки и без скролла. Демо
      // фиксированно 16:9, поэтому на широком экране по бокам остаются поля
      // (нормальный леттербокс); кадр центрируется (justify-center на outerRef).
      const containerH = outerRef.current.clientHeight || 0;
      const aspect = naturalW / naturalH;
      let w = containerW;
      let h = w / aspect;
      if (containerH > 0 && h > containerH) {
        h = containerH;
        w = h * aspect;
      }
      setSize({ w, h });
    };
    update();
    const ro = new ResizeObserver(update);
    if (outerRef.current) ro.observe(outerRef.current);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [naturalW, naturalH]);

  const scale = size.w > 0 ? size.w / naturalW : 1;

  // Считаем zoom-трансформ для всего «сцена-кадра» (iframe + overlay'ов).
  // box задан в natural-координатах. Хотим: точка центра box оказывается
  // в центре видимой области, и box целиком вписывается в кадр с zoomScale.
  // Limits: zoomScale ≤ 6 (чтобы текст не разваливался), и >= 1.4
  // (минимальная заметная цель). Padding 60px вокруг box — не упирается
  // в края.
  const PAD = 60;
  let zoomScale = 1;
  let zoomTx = 0;
  let zoomTy = 0;
  if (zoom && size.w > 0) {
    const [bx, by, bw, bh] = zoom.box;
    const sx = (naturalW) / (bw + PAD * 2);
    const sy = (naturalH) / (bh + PAD * 2);
    zoomScale = Math.max(1.4, Math.min(6, Math.min(sx, sy)));
    const boxCenterX = bx + bw / 2;
    const boxCenterY = by + bh / 2;
    // После scale(zoomScale) от центра кадра: сдвиг = (центр_кадра — центр_box) * scale
    zoomTx = (naturalW / 2 - boxCenterX) * zoomScale;
    zoomTy = (naturalH / 2 - boxCenterY) * zoomScale;
  }

  return (
    <div ref={outerRef} className="w-full h-full flex justify-center items-start">
      <div
        className="relative rounded-lg overflow-hidden bg-zinc-800"
        style={{ width: `${size.w}px`, height: `${size.h}px` }}
      >
        {/* Внутренняя «сцена» — её мы крутим под zoom. iframe и оверлеи
            рендерятся в natural-координатах внутри неё. */}
        <div
          className="absolute top-0 left-0"
          style={{
            width: `${naturalW}px`,
            height: `${naturalH}px`,
            transform: `scale(${scale}) translate(${zoomTx / scale}px, ${zoomTy / scale}px) scale(${zoomScale})`,
            transformOrigin: '0 0',
            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <iframe
            ref={iframeRef}
            src={lockedSrc}
            title={title}
            className="absolute top-0 left-0 border-0 bg-white"
            style={{
              width: `${naturalW}px`,
              height: `${naturalH}px`,
              pointerEvents: interactive ? 'auto' : 'none',
            }}
          />
          {children}
        </div>
      </div>
    </div>
  );
};

// =====================================================
// BrowseOverlay — initial dim state. Whole iframe darkened except sidebar.
// Shows a "Start training" button in the center of non-sidebar area.
// User can switch sections via sidebar; click Start to begin training.
// =====================================================
const BrowseOverlay: React.FC<{
  sidebarBounds: { x: number; y: number; w: number; h: number };
  imgSize: { w: number; h: number };
  stageTitle: string;
  alreadyCompleted: boolean;
  canProceed: boolean;
  onStart: () => void;
  onNext: () => void;
  onSelfPractice: () => void;
}> = ({ sidebarBounds, imgSize, stageTitle, alreadyCompleted, canProceed, onStart, onNext, onSelfPractice }) => {
  const xPct = (sidebarBounds.x / imgSize.w) * 100;
  const yPct = (sidebarBounds.y / imgSize.h) * 100;
  const wPct = (sidebarBounds.w / imgSize.w) * 100;
  const hPct = (sidebarBounds.h / imgSize.h) * 100;
  const blocker: React.CSSProperties = {
    position: 'absolute',
    background: 'rgba(0, 0, 0, 0.78)',
    pointerEvents: 'auto',
    cursor: 'not-allowed',
    backdropFilter: 'grayscale(0.4)',
  };
  return (
    <>
      {/* 4 dark blockers around sidebar — sidebar stays interactive */}
      <div className="z-10" style={{ ...blocker, left: 0, top: 0, right: 0, height: `${yPct}%` }} />
      <div className="z-10" style={{ ...blocker, left: 0, top: `${yPct + hPct}%`, right: 0, bottom: 0 }} />
      <div className="z-10" style={{ ...blocker, left: 0, top: `${yPct}%`, width: `${xPct}%`, height: `${hPct}%` }} />
      <div className="z-10" style={{ ...blocker, left: `${xPct + wPct}%`, top: `${yPct}%`, right: 0, height: `${hPct}%` }} />

      {/* Center buttons — positioned in non-sidebar area */}
      <div
        className="absolute z-30 flex items-center justify-center pointer-events-none"
        style={{
          left: `${xPct + wPct}%`,
          top: `${yPct}%`,
          right: 0,
          height: `${hPct}%`,
        }}
      >
        {alreadyCompleted ? (
          <div className="flex flex-col gap-3 items-stretch pointer-events-auto" style={{ width: 360 }}>
            {canProceed && (
              <button
                onClick={onNext}
                className="rounded-xl px-6 py-4 font-semibold text-white text-base flex items-center justify-center gap-3 transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  boxShadow: '0 10px 28px rgba(16,185,129,0.45)',
                }}
              >
                <ArrowRight className="h-5 w-5" />
                Перейти к следующему этапу
              </button>
            )}
            <button
              onClick={onSelfPractice}
              className="rounded-xl px-6 py-4 font-semibold text-zinc-100 text-base flex items-center justify-center gap-3 transition-all hover:bg-fill-2"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.14)',
              }}
            >
              Пройти самостоятельно
            </button>
          </div>
        ) : (
          <button
            onClick={onStart}
            className="pointer-events-auto rounded-2xl px-8 py-5 font-bold text-white text-lg flex items-center gap-3 transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              boxShadow: '0 10px 40px rgba(99,102,241,0.5), 0 0 0 4px rgba(99,102,241,0.2)',
            }}
          >
            <Play className="h-6 w-6 fill-current" />
            <div className="text-left">
              <div>Запустить обучение</div>
              <div className="text-xs font-normal opacity-80">{stageTitle}</div>
            </div>
          </button>
        )}
      </div>
    </>
  );
};

// =====================================================
// Spotlight overlay — darkens everything except hotspot
// =====================================================
const SpotlightOverlay: React.FC<{
  hotspot: TrainingHotspot;
  imgSize: { w: number; h: number };
  hint: boolean;
  onMissClick: () => void;
}> = ({ hotspot, imgSize, hint, onMissClick }) => {
  const [bx, by, bw, bh] = hotspot.box;
  const x = (bx / imgSize.w) * 100;
  const y = (by / imgSize.h) * 100;
  const w = (bw / imgSize.w) * 100;
  const h = (bh / imgSize.h) * 100;

  const blockerStyle: React.CSSProperties = {
    position: 'absolute',
    background: 'rgba(0, 0, 0, 0.72)',
    pointerEvents: 'auto',
    cursor: 'not-allowed',
    backdropFilter: 'grayscale(0.6) blur(0.5px)',
  };

  return (
    <>
      {/* 4 dark blockers around the hotspot — block clicks everywhere except hotspot */}
      {/* Top */}
      <div
        onClick={onMissClick}
        className="z-10"
        style={{ ...blockerStyle, left: 0, top: 0, right: 0, height: `${y}%` }}
      />
      {/* Bottom */}
      <div
        onClick={onMissClick}
        className="z-10"
        style={{ ...blockerStyle, left: 0, top: `${y + h}%`, right: 0, bottom: 0 }}
      />
      {/* Left */}
      <div
        onClick={onMissClick}
        className="z-10"
        style={{
          ...blockerStyle,
          left: 0,
          top: `${y}%`,
          width: `${x}%`,
          height: `${h}%`,
        }}
      />
      {/* Right */}
      <div
        onClick={onMissClick}
        className="z-10"
        style={{
          ...blockerStyle,
          left: `${x + w}%`,
          top: `${y}%`,
          right: 0,
          height: `${h}%`,
        }}
      />

      {/* Pulsing ring around hotspot (visual only) */}
      <div
        className={`absolute z-20 rounded-md pointer-events-none ${hint ? 'animate-pulse' : ''}`}
        style={{
          left: `${x}%`,
          top: `${y}%`,
          width: `${w}%`,
          height: `${h}%`,
          border: '2px dashed #a78bfa',
          animation: 'training-pulse 1.6s ease-in-out infinite',
        }}
      />

      {/* No click capture — real clicks pass through hotspot directly to the iframe */}

      <style>{`
        @keyframes training-pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(255,215,0,0.25), 0 0 16px rgba(255,215,0,0.5); }
          50% { box-shadow: 0 0 0 8px rgba(255,215,0,0.35), 0 0 32px rgba(255,215,0,0.85); }
        }
      `}</style>
    </>
  );
};

export default TrainingPage;
